/**
 * Alpine.js Global Stores
 *
 * Provides centralized state management for UI orchestration.
 * Loaded before Alpine.js initializes.
 *
 * Architecture: See specs/architecture-ui.md
 */

document.addEventListener('alpine:init', () => {
  // Check for ?debug param before Alpine initializes
  const shouldOpenDebug = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug');

  // UI State Store - Manages layout, panels, navigation
  Alpine.store('ui', {
    // Panel navigation (desktop sidebar)
    activePanel: shouldOpenDebug ? 'debug' : 'calcs',

    // Calc toolbar visibility (can be hidden via More menu)
    calcToolbarHidden: false,

    // Bottom navigation (desktop floating nav)
    // Default to true so floating nav is the default behavior on desktop.
    bottomNavEnabled: true,
    bottomNavCollapsed: false,

    // Mobile navigation
    mobiletab: null,

    // Dock visibility (prevents FOUC)
    dockReady: false,

    // Hover states for sidebars (used for collapse button visibility)
    sidebarHover: false,
    panelHover: false,

    // Left Sidebar State (Reactive)
    leftSidebarCollapsed: false,

    // Track WHY sidebar is collapsed for smart restore behavior
    // 'user' = user manually collapsed, 'auto' = auto-collapsed for right panel, 'view' = collapsed due to view type
    sidebarCollapseReason: null,

    // Right rail panels that trigger auto-collapse of left sidebar
    rightRailPanels: (() => {
      const panels = ['theme', 'help', 'calcy', 'keypad', 'gallery', 'preview', 'debug'];
      if (window.IC_CONFIG?.features?.community) {
        panels.push('collab');
      }
      return panels;
    })(),

    // Actions - Panel Management
    open(panel, force = false) {
      if (this.activePanel === panel && !force) {
        window.dispatchEvent(new CustomEvent('panel-bounce', { detail: { panel } }));
        return;
      }
      this.activePanel = panel;
    },

    // NEW: Smart panel open with auto-collapse behavior
    // Re-entrancy guard to prevent double-calls during Alpine reactivity
    _openPanelInProgress: false,

    openPanel(panel) {
      // Guard against re-entrancy (Alpine reactivity can cause double-calls)
      if (this._openPanelInProgress) return;
      this._openPanelInProgress = true;

      try {
        // If same panel, toggle it closed
        if (this.activePanel === panel) {
          this.closePanel();
          return;
        }

        this.activePanel = panel;

        // Auto-collapse left sidebar when opening right panel (but not on shared views)
        if (this.rightRailPanels.includes(panel) && !this.leftSidebarCollapsed && !window.Router?.isSharedView?.()) {
          this.leftSidebarCollapsed = true;
          this.sidebarCollapseReason = 'auto';
        }
      } finally {
        this._openPanelInProgress = false;
      }
    },

    // NEW: Smart panel close with auto-restore behavior
    closePanel() {
      this.activePanel = null;

      // Auto-restore left sidebar only if it was auto-collapsed (never on shared views)
      if (this.sidebarCollapseReason === 'auto' && !window.Router?.isSharedView?.()) {
        this.leftSidebarCollapsed = false;
        this.sidebarCollapseReason = null;
      }
    },

    collapse() {
      this.closePanel();
    },

    closeAll() {
      this.activePanel = 'calcs';
    },

    // NEW: Sidebar management with reason tracking
    collapseSidebar(reason = 'user') {
      this.leftSidebarCollapsed = true;
      this.sidebarCollapseReason = reason;
    },

    expandSidebar() {
      this.leftSidebarCollapsed = false;
      this.sidebarCollapseReason = null;
    },

    toggleSidebar() {
      if (this.leftSidebarCollapsed) {
        this.expandSidebar();
      } else {
        this.collapseSidebar('user');
      }
    },

    toggleBottomNavCollapse() {
      this.bottomNavCollapsed = !this.bottomNavCollapsed;
      if (window.SettingsManager) {
        window.SettingsManager.set('ui.bottomNavCollapsed', this.bottomNavCollapsed);
      } else {
        localStorage.setItem('bottomNavCollapsed', String(this.bottomNavCollapsed));
      }
    },

    hideCalcToolbar() {
      this.calcToolbarHidden = true;
    },

    showCalcToolbar() {
      this.calcToolbarHidden = false;
    },

    closeMobilePanel() {
      this.mobiletab = null;
    },

    openMobilePanel(panel) {
      this.mobiletab = this.mobiletab === panel ? null : panel;
    },

    // Legacy methods - delegate to new methods
    toggleLeftSidebar() {
      this.toggleSidebar();
    },

    setLeftSidebarCollapsed(collapsed) {
      if (collapsed) {
        this.collapseSidebar('user');
      } else {
        this.expandSidebar();
      }
    },

    // Initialize from localStorage
    init() {
      // Restore bottom nav state
      if (window.SettingsManager) {
        const savedCollapsed = window.SettingsManager.get('ui.bottomNavCollapsed');
        if (savedCollapsed !== undefined) {
          this.bottomNavCollapsed = savedCollapsed;
        }
      } else {
        const savedCollapsed = localStorage.getItem('bottomNavCollapsed');
        if (savedCollapsed !== null) {
          this.bottomNavCollapsed = savedCollapsed === 'true';
        }
      }

      // Helper to apply theme preference (if explicitly provided) or fallback
      const applyThemePref = () => {
        if (typeof window.themeConfig?.showBottomNav === 'boolean') {
          this.bottomNavEnabled = window.themeConfig.showBottomNav;
        } else {
          // default behaviour: enable floating bottom nav
          this.bottomNavEnabled = true;
        }
      };

      // Apply initial theme preference immediately (avoids race with page-level init)
      applyThemePref();

      // Sync when crossing the desktop breakpoint so mobile->desktop resizes respect the preference
      try {
        const mq = window.matchMedia('(min-width: 1024px)');
        const mqHandler = (ev) => {
          if (ev.matches) applyThemePref();
        };
        if (mq.addEventListener) mq.addEventListener('change', mqHandler);
        else if (mq.addListener) mq.addListener(mqHandler);
      } catch (e) {
        // matchMedia may not be available in some test environments — ignore
      }
    }
  });

  // Calc State Store - Read-only mirror of React state
  // React updates this via events, Alpine reads it for display
  Alpine.store('calc', {
    selectedId: null,
    hasUnsavedChanges: false,

    // Update from React
    setSelected(id) {
      this.selectedId = id;
    },

    setUnsavedChanges(hasChanges) {
      this.hasUnsavedChanges = hasChanges;
    }
  });

  // Initialize UI store
  Alpine.store('ui').init();

  // Subscribe to CALC_SWITCH from CalcStore
  // This keeps Alpine's calc store in sync with the canonical selection
  if (window.InstacalcEvents) {
    const eventName = window.InstacalcEvents.CALC_SWITCH;
    window.InstacalcEvents.on(eventName, (e) => {
      const { calcId } = e.detail || {};
      if (calcId) {
        Alpine.store('calc').setSelected(calcId);
      }
    });
    if (window.Debug) window.Debug.log('ui', 'Subscribed to CALC_SWITCH');

    // Subscribe to CALC_TOOLBAR_HIDE to hide the toolbar
    window.InstacalcEvents.on(window.InstacalcEvents.CALC_TOOLBAR_HIDE, () => {
      Alpine.store('ui').hideCalcToolbar();
    });
  }
});
;
// Mobile Nav Icons - Using size-6 (24px) matching desktop dock
// Same SVG paths as desktop dock icons (panel-icons.html)
window.mobileNavIcons = {
    keypad: `<svg class="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="1.3574711" y="3.2405481" width="19.771212" height="19.438904" rx="4.9428029" fill="none" stroke="currentColor" stroke-width="1.47033" />
    <g transform="matrix(1.1208805,0,0,1.1478475,-2.1594742,-0.63410138)">
      <g transform="translate(0.3966196,0.6588002)">
        <g transform="matrix(-0.7241189,0.70710678,-0.70710678,-0.69049434,11.921202,7.9937231)">
          <path d="M 1.53,0.47 C 1.24,0.18 0.76,0.18 0.47,0.47 0.18,0.76 0.18,1.24 0.47,1.53 L 1.41,2.47 0.47,3.41 c -0.29,0.29 -0.29,0.77 0,1.06 0.29,0.29 0.77,0.29 1.06,0 L 2.47,3.53 3.41,4.47 c 0.29,0.29 0.77,0.29 1.06,0 0.29,-0.29 0.29,-0.77 0,-1.06 L 3.53,2.47 4.47,1.53 C 4.76,1.24 4.76,0.76 4.47,0.47 4.18,0.18 3.7,0.18 3.41,0.47 L 2.47,1.41 Z" fill="currentColor" />
        </g>
        <rect x="12.597517" y="7.2847557" width="5" height="1.5" rx="0.75" fill="currentColor" />
        <g transform="rotate(90,-0.56345969,11.419534)">
          <path d="M 1.53,0.47 C 1.24,0.18 0.76,0.18 0.47,0.47 0.18,0.76 0.18,1.24 0.47,1.53 L 1.41,2.47 0.47,3.41 c -0.29,0.29 -0.29,0.77 0,1.06 0.29,0.29 0.77,0.29 1.06,0 L 2.47,3.53 3.41,4.47 c 0.29,0.29 0.77,0.29 1.06,0 0.29,-0.29 0.29,-0.77 0,-1.06 L 3.53,2.47 4.47,1.53 C 4.76,1.24 4.76,0.76 4.47,0.47 4.18,0.18 3.7,0.18 3.41,0.47 L 2.47,1.41 Z" fill="currentColor" />
        </g>
        <g fill="currentColor" transform="translate(-1.4024828,-1.4731604)">
          <circle cx="16.5" cy="14" r="0.75" />
          <rect x="14" y="15.176154" width="5" height="1.5" rx="0.75" />
          <circle cx="16.5" cy="17.852308" r="0.75" />
        </g>
      </g>
    </g>
  </svg>`,
    toolbar: `<svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 2.25h4.5l.452 2.713a6.75 6.75 0 0 1 2.335 1.348l2.532-.844 2.25 3.897-2.08 1.52a6.778 6.778 0 0 1 0 2.634l2.08 1.52-2.25 3.897-2.532-.844a6.75 6.75 0 0 1-2.335 1.348l-.452 2.713h-4.5l-.452-2.713a6.75 6.75 0 0 1-2.335-1.348l-2.532.844-2.25-3.897 2.08-1.52a6.778 6.778 0 0 1 0-2.634l-2.08-1.52 2.25-3.897 2.532.844a6.75 6.75 0 0 1 2.335-1.348l.452-2.713z" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>`,
    moon: `<svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
  </svg>`,
    calcs: `<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
  </svg>`,
    help: `<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
  </svg>`,
    info: `<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
  </svg>`,
    back: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
  </svg>`,
    externalLink: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
  </svg>`,
    link: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
  </svg>`,
    code: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"/>
  </svg>`,
    clipboard: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"/>
  </svg>`,
    check: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
  </svg>`,
    save: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
  </svg>`,
    close: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
  </svg>`,
    remix: `<svg class="size-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3"/>
  </svg>`,
    // Icons for index.html sidebar/navigation
    plus: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
  </svg>`,
    chevronDoubleRight: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
  </svg>`,
    chevronDoubleLeft: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
  </svg>`,
    chevronDown: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
  </svg>`,
    infoCircle: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
  </svg>`,
    hamburger: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
  </svg>`,
    ellipsis: `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" class="size-6">
    <circle cx="6" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="18" cy="12" r="1.5"/>
  </svg>`,
    users: `<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>`
};
// UI Class Strings - Centralized Tailwind classes to avoid repetition
function uiClasses() {
    return {
        // Panel Cards (all panels are absolutely positioned to stack cleanly)
        panelCard: 'rounded-3xl bg-[var(--sidebar-card-bg)] backdrop-blur-xl border border-[var(--sidebar-card-border)]',
        panelCardScrollable: 'absolute inset-0 group flex flex-col p-6 overflow-y-auto overflow-x-visible no-scrollbar rounded-3xl bg-[var(--sidebar-card-bg)] backdrop-blur-xl border border-[var(--sidebar-card-border)] shadow-sidebar-card',
        panelCardSimple: 'absolute inset-0 flex flex-col rounded-3xl p-4 bg-[var(--sidebar-card-bg)] backdrop-blur-xl border border-[var(--sidebar-card-border)] shadow-sidebar-card',

        // Navigation Buttons
        navButton: 'nav-button flex items-center justify-center transition-all duration-slow border border-transparent rounded-full w-9 h-9 hover:bg-theme-bg-secondary hover:border-theme-border text-theme-text tooltip select-none',

        // Close Buttons
        closeButton: 'absolute flex items-center justify-center w-6 h-6 transition-opacity duration-fast-normal rounded-full opacity-0 top-2 right-2 hover:opacity-100',
        closeButtonStyle: 'background: var(--accent-medium); color: var(--text);',

        // Panel transition attributes (simple opacity fade - panels are already absolutely positioned)
        panelSlideTransition: {
            'x-transition:enter': 'transition-opacity duration-fast-medium',
            'x-transition:enter-start': 'opacity-0',
            'x-transition:enter-end': 'opacity-100',
            'x-transition:leave': 'transition-opacity duration-fast-normal-ease',
            'x-transition:leave-start': 'opacity-100',
            'x-transition:leave-end': 'opacity-0'
        },

        // Expand/collapse transition (for bottom nav items)
        expandCollapseTransition: {
            'x-transition:enter': 'expand-collapse-enter',
            'x-transition:enter-start': 'expand-collapse-enter-start',
            'x-transition:enter-end': 'expand-collapse-enter-end',
            'x-transition:leave': 'expand-collapse-leave',
            'x-transition:leave-start': 'expand-collapse-leave-start',
            'x-transition:leave-end': 'expand-collapse-leave-end'
        },

        // Icon spin transition
        iconSpinTransition: {
            'x-transition:enter': 'transition-transform duration-500 ease-in-out',
            'x-transition:enter-start': 'rotate-0',
            'x-transition:enter-end': 'rotate-180',
            'x-transition:leave': 'transition-transform duration-500 ease-in-out',
            'x-transition:leave-start': 'rotate-180',
            'x-transition:leave-end': 'rotate-0'
        },

        // Dock Style Helper
        getDockStyles(dockStyle, borderStyle) {
            // Base styles (Background, Padding, Radius, Shadow) - NO BORDERS here
            const classes = {
                'glass': 'p-3 rounded-3xl bg-theme-calc-bg/50 backdrop-blur-xl shadow-lg',
                'solid': 'p-3 rounded-3xl bg-theme-calc-bg shadow-xl',
                'outlined': 'p-3 rounded-3xl', // Transparent background
                'segmented': 'p-2 rounded-2xl bg-theme-calc-bg/50 backdrop-blur-md',
                'floating': 'p-4 rounded-full bg-theme-calc-bg/80 backdrop-blur-lg shadow-2xl',
                'minimal': '',
                'compact': 'p-2 rounded-2xl bg-theme-bg-secondary',
                'ios': 'p-3 rounded-[2rem] bg-theme-calc-bg/80 backdrop-blur-2xl shadow-sm',
                'neumorphic': 'p-3 rounded-3xl bg-theme-bg shadow-[8px_8px_16px_rgba(0,0,0,0.2),-8px_-8px_16px_rgba(255,255,255,0.05)]',
            };

            // Gradient styles share the same base class
            if (dockStyle.startsWith('gradient-')) {
                classes[dockStyle] = 'p-3 rounded-3xl backdrop-blur-xl shadow-lg';
            }

            let className = classes[dockStyle] || '';
            let style = '';

            // Border Styles - Applied on top of dock style
            if (borderStyle === 'none') {
                className += ' border-0';
            }
            else if (borderStyle === 'subtle') {
                className += ' border';
                style += 'border-color: color-mix(in srgb, var(--theme-border) 20%, transparent); ';
            }
            else if (borderStyle === 'glass') {
                className += ' border-2 shadow-[0_0_20px_rgba(255,255,255,0.1)]';
                style += 'border-color: var(--glass-border-color); ';
            }
            else if (borderStyle === 'gradient') {
                className += ' border-2';
                style += 'border-image: linear-gradient(135deg, var(--accent), var(--accent-secondary, var(--accent))) 1; ';
            }

            // Gradient Backgrounds
            if (dockStyle === 'gradient-1') style += 'background: linear-gradient(135deg, color-mix(in srgb, var(--accent) 30%, transparent), color-mix(in srgb, var(--accent-secondary, var(--accent)) 25%, transparent), color-mix(in srgb, var(--calc-bg-color) 40%, transparent)); ';
            else if (dockStyle === 'gradient-2') style += 'background: linear-gradient(135deg, color-mix(in srgb, var(--accent) 25%, transparent), color-mix(in srgb, var(--bg-secondary-mix-40) 30%, transparent), color-mix(in srgb, var(--accent-secondary, var(--accent)) 20%, transparent)); ';
            else if (dockStyle === 'gradient-3') style += 'background: linear-gradient(135deg, color-mix(in srgb, var(--accent-secondary, var(--accent)) 30%, transparent), color-mix(in srgb, var(--accent) 25%, transparent), color-mix(in srgb, var(--calc-bg-color) 35%, transparent)); ';
            else if (dockStyle === 'gradient-4') style += 'background: linear-gradient(135deg, color-mix(in srgb, var(--accent) 28%, transparent), color-mix(in srgb, var(--calc-bg-color) 35%, transparent), color-mix(in srgb, var(--accent-secondary, var(--accent)) 22%, transparent)); ';
            else if (dockStyle === 'gradient-5') style += 'background: linear-gradient(135deg, rgba(224, 215, 255, 0.9), rgba(210, 225, 255, 0.9)); box-shadow: 0 8px 32px rgba(100, 100, 255, 0.15); '; // Removed inline border

            return { class: className, style: style };
        },

        // Icon spin reverse transition (opposite direction)
        iconSpinReverseTransition: {
            'x-transition:enter': 'icon-spin-reverse-enter',
            'x-transition:enter-start': 'icon-spin-reverse-enter-start',
            'x-transition:enter-end': 'icon-spin-reverse-enter-end',
            'x-transition:leave': 'icon-spin-reverse-leave',
            'x-transition:leave-start': 'icon-spin-reverse-leave-start',
            'x-transition:leave-end': 'icon-spin-reverse-leave-end'
        },

        // Aside panel slide transition
        asidePanelTransition: {
            'x-transition:enter': 'transition-all duration-slow-out',
            'x-transition:enter-start': 'opacity-0 translate-x-8',
            'x-transition:enter-end': 'opacity-100 translate-x-0',
            'x-transition:leave': 'transition-all duration-slow-in',
            'x-transition:leave-start': 'opacity-100 translate-x-0',
            'x-transition:leave-end': 'opacity-0 translate-x-8'
        }
    };
}
window.uiClasses = uiClasses;
if (!window.SidebarManager) {
    /**
     * SidebarManager - Thin orchestration layer for sidebar behavior
     *
     * All state is managed in Alpine store ($store.ui). This class:
     * 1. Initializes sidebar state based on view type
     * 2. Handles events that need sidebar changes
     * 3. Provides a simple API for components to use
     *
     * State and auto-restore logic lives in alpine-store.js
     */
    window.SidebarManager = class SidebarManager {
        constructor() {
            // Initialize sidebar collapsed state based on view type
            const isSharedView = window.Router?.isSharedView?.() || false;
            this._initialCollapsed = isSharedView;

            // Listen for events that should collapse sidebar
            window.InstacalcEvents.on(window.InstacalcEvents.HELP_OPEN_TOUR, (e) => {
                if (e.detail?.collapseLeft) {
                    this.store?.collapseSidebar('auto');
                }
            });

            window.InstacalcEvents.on(window.InstacalcEvents.SIDEBAR_COLLAPSE, () => {
                this.store?.collapseSidebar('view');
            });

            // Apply initial state once Alpine is ready
            document.addEventListener('alpine:initialized', () => {
                if (this._initialCollapsed && this.store) {
                    this.store.collapseSidebar('view');
                }
            }, { once: true });
        }

        get store() {
            return window.Alpine?.store('ui');
        }

        get activePanel() {
            return this.store?.activePanel;
        }

        get leftCollapsed() {
            return this.store?.leftSidebarCollapsed ?? this._initialCollapsed;
        }

        set leftCollapsed(value) {
            if (value) {
                this.store?.collapseSidebar('user');
            } else {
                this.store?.expandSidebar();
            }
        }

        // Delegate all operations to store
        toggleLeft() {
            this.store?.toggleSidebar();
        }

        toggleRight(panelName) {
            this.store?.openPanel(panelName);
        }

        openRight(panelName) {
            this.store?.openPanel(panelName);
        }

        closeRight() {
            this.store?.closePanel();
        }

        closeAll() {
            this.store?.closeAll();
        }
    }
}

function mainLayoutData() {
    const icons = window.mobileNavIcons || { keypad: '', toolbar: '', calcs: '', help: '' };
    const uiClassesSafe = typeof uiClasses === 'function' ? uiClasses : () => ({});

    const getUIStore = () => {
        if (window.Alpine?.store) return window.Alpine.store('ui');
        return {
            activePanel: null,
            bottomNavEnabled: false,
            bottomNavCollapsed: false,
            mobiletab: false,
            open() { },
            collapse() { },
            closeAll() { },
            closeMobilePanel() { },
            toggleBottomNavCollapse() { },
        };
    };

    // Initialize manager
    const sidebarManager = new SidebarManager();

    const sortPanels = (panels = []) => panels.slice().sort((a, b) => {
        const left = typeof a.order === 'number' ? a.order : 99;
        const right = typeof b.order === 'number' ? b.order : 99;
        return left - right;
    });

    const isPanelEnabled = (panel) => {
        if (!panel) return false;
        if (panel.enabled === false) return false;
        if (typeof panel.enabled === 'function') {
            try {
                return panel.enabled({ features: window.IC_CONFIG?.features });
            } catch (err) {
                console.warn('[layout] panel enabled() threw', err);
                return false;
            }
        }
        return true;
    };

    const registryPanels = (() => {
        const registry = window.__IC_PLUGINS__?.panels;
        if (!registry) return [];
        const panels = typeof registry.getAllSorted === 'function'
            ? registry.getAllSorted()
            : registry.getAll?.() || [];
        return sortPanels(panels).filter(isPanelEnabled);
    })();

    const mapPanelId = (panel) => panel.mobileId || (panel.id === 'keypad' ? 'buttons' : panel.id);

    const pluginDockPanels = sortPanels(registryPanels.filter(panel => panel.showInDock));
    const pluginNavTabs = pluginDockPanels.map(panel => ({
        id: mapPanelId(panel),
        label: panel.label,
        icon: panel.icon,
        beta: panel.beta,
        betaTooltip: panel.betaTooltip
    }));
    const pluginMobilePanels = pluginDockPanels.map(panel => ({
        id: mapPanelId(panel),
        load: panel.templateUrl,
        innerClass: panel.innerClass
    })).filter(panel => panel.load);

    const baseNavTabs = [
        { id: 'buttons', label: 'Keypad', icon: icons.keypad },
        { id: 'toolbar', label: 'Toolbar', icon: icons.toolbar },
        { id: 'calcs', label: 'Calcs', icon: icons.calcs },
        { id: 'calcy', label: 'Calcy AI', icon: 'calcy-image' },
        { id: 'help', label: 'Help', icon: icons.help }
    ];

    const basePanels = [
        { id: 'buttons', load: 'templates/keypad.html' },
        { id: 'calcs', component: 'MobileCalcList' },
        { id: 'calcy', load: 'templates/calcy.html' },
        { id: 'help', load: 'templates/help-panel.html' },
        { id: 'info', load: 'templates/info-popover.html' },
        { id: 'toolbar', inline: true } // Special case for inline content
    ];

    if (window.IC_CONFIG?.features?.community) {
        baseNavTabs.push({ id: 'collab', label: 'Community', icon: 'globe' });
        basePanels.push({ id: 'collab', load: 'templates/collab-panel.html', innerClass: 'h-full' });
    }

    const mergeById = (base, overrides) => {
        const merged = base.map(item => ({ ...item }));
        overrides.forEach(override => {
            const idx = merged.findIndex(item => item.id === override.id);
            if (idx >= 0) {
                merged[idx] = { ...merged[idx], ...override };
            } else {
                merged.push({ ...override });
            }
        });
        return merged;
    };

    const mobileNavTabs = pluginNavTabs.length ? mergeById(baseNavTabs, pluginNavTabs) : baseNavTabs;
    const mobilePanels = pluginMobilePanels.length ? mergeById(basePanels, pluginMobilePanels) : basePanels;

    return {
        // Expose manager state and methods
        sidebarManager,
        get leftSidebarCollapsed() { return Alpine.store('ui').leftSidebarCollapsed; },
        set leftSidebarCollapsed(val) { Alpine.store('ui').leftSidebarCollapsed = val; },

        toggleLeftSidebar() { this.sidebarManager.toggleLeft(); },

        // Proxy to Alpine store
        get activePanel() { return getUIStore().activePanel; },
        set activePanel(val) { getUIStore().activePanel = val; },
        get bottomNavEnabled() { return getUIStore().bottomNavEnabled; },
        set bottomNavEnabled(val) { getUIStore().bottomNavEnabled = val; },
        get bottomNavCollapsed() { return getUIStore().bottomNavCollapsed; },
        set bottomNavCollapsed(val) { getUIStore().bottomNavCollapsed = val; },
        get mobiletab() { return getUIStore().mobiletab; },
        set mobiletab(val) { getUIStore().mobiletab = val; },

        // Local UI constants
        mobileNavTabs,
        topNavItems: [
            { label: 'App', active: true },
            { label: 'Shared', active: false, tip: 'Coming soon' }
        ],
        mobilePanels,
        ui: uiClassesSafe(),

        // Dock Theme Logic (Shared)
        dockStyle: 'glass',
        dockButtonStyle: 'soft',
        dockButtonSpacing: 'normal',
        borderStyle: 'none',

        // Dock positioning - synced from ThemeSystem
        panelPosition: (() => {
            const pos = window.ThemeSystem?.getDockPosition?.() || 'center';
            const map = { 'left': 'bottom-left', 'center': 'bottom-center', 'right': 'bottom-right' };
            return map[pos] || 'bottom-center';
        })(),
        settingsOpen: false,

        updateDockGradientVar() {
            const vars = this.dockThemeVars;
            const bgImageMatch = vars.match(/--dock-bg-image:\s*([^;]+);/);
            if (bgImageMatch) {
                document.documentElement.style.setProperty('--active-dock-gradient', bgImageMatch[1]);
            }
        },

        getDockThemeConfig() {
            const themes = {
                'glass': { bg: 'var(--calc-bg-color)', bgOpacity: '0.6', backdrop: 'blur(24px)', shadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', radius: '1.5rem', padding: '0.75rem' },
                'solid': { bg: 'var(--calc-bg-color)', bgOpacity: '1', backdrop: 'none', shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', radius: '1.5rem', padding: '0.75rem' },
                'outlined': { bg: 'transparent', bgOpacity: '0', backdrop: 'none', shadow: 'none', radius: '1.5rem', padding: '0.75rem' },
                'segmented': { bg: 'var(--calc-bg-color)', bgOpacity: '0.4', backdrop: 'blur(12px)', shadow: 'none', radius: '1rem', padding: '0.5rem' },
                'floating': { bg: 'var(--calc-bg-color)', bgOpacity: '0.7', backdrop: 'blur(16px)', shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', radius: '9999px', padding: '1rem' },
                'minimal': { bg: 'transparent', bgOpacity: '0', backdrop: 'none', shadow: 'none', radius: '0', padding: '0' },
                'compact': { bg: 'var(--theme-bg-secondary)', bgOpacity: '1', backdrop: 'none', shadow: 'none', radius: '1rem', padding: '0.5rem' },
                'ios': { bg: 'var(--calc-bg-color)', bgOpacity: '0.8', backdrop: 'blur(40px)', shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', radius: '2rem', padding: '0.75rem' },
                'neumorphic': { bg: 'var(--theme-bg)', bgOpacity: '1', backdrop: 'none', shadow: '8px 8px 16px rgba(0,0,0,0.2), -8px -8px 16px rgba(255,255,255,0.05)', radius: '1.5rem', padding: '0.75rem' },
            };

            // Default to glass if not found
            let theme = themes[this.dockStyle] || themes['glass'];

            // Gradient Overrides
            if (this.dockStyle.startsWith('gradient-')) {
                theme = { ...themes['glass'], bgOpacity: '0', shadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }; // Base for gradients

                // Get dock gradient mix percentage from CSS variable (set by gradient intensity system)
                // Fallback to 15% if getComputedStyle fails (test environment)
                let dockMix = '15%';
                try {
                    const val = getComputedStyle(document.documentElement).getPropertyValue('--dock-gradient-mix');
                    if (val) dockMix = val.trim() || '15%';
                } catch (e) { /* test environment fallback */ }

                const gradients = {
                    'gradient-1': 'linear-gradient(135deg, var(--accent), var(--accent-bright))',
                    'gradient-2': 'linear-gradient(to right, var(--accent), var(--accent-bright), var(--accent-tertiary))',
                    'gradient-3': 'radial-gradient(circle at top left, var(--accent), transparent 70%), radial-gradient(circle at bottom right, var(--accent-bright), transparent 70%), var(--calc-bg-color)',
                    'gradient-4': 'linear-gradient(180deg, color-mix(in srgb, var(--accent) 80%, transparent), var(--calc-bg-color))',
                    'gradient-5': 'linear-gradient(135deg, var(--dock-gradient-5-start), var(--dock-gradient-5-end))',
                    'gradient-5a': 'linear-gradient(135deg, var(--accent), var(--accent-bright))',
                    'gradient-5b': 'linear-gradient(135deg, var(--accent-bright), var(--accent-tertiary))',
                    'gradient-5c': `linear-gradient(135deg, color-mix(in srgb, var(--text-dim) ${dockMix}, var(--calc-bg-color)) 0%, color-mix(in srgb, var(--accent) ${dockMix}, var(--calc-bg-color)) 50%, color-mix(in srgb, var(--accent-bright) ${dockMix}, var(--calc-bg-color)) 100%)`,
                    'gradient-5d': 'linear-gradient(135deg, color-mix(in srgb, var(--accent) 60%, var(--calc-bg-color)), color-mix(in srgb, var(--accent-bright) 60%, var(--calc-bg-color)))',
                    'gradient-5e': 'linear-gradient(to right, var(--accent), var(--calc-bg-color))',
                    'gradient-5f': 'linear-gradient(to left, var(--accent-bright), var(--calc-bg-color))',
                    'gradient-6': 'linear-gradient(165deg, var(--accent), var(--accent-bright), var(--accent-tertiary))',
                    'gradient-7': 'linear-gradient(45deg, var(--calc-bg-color), var(--accent))',
                    'gradient-8': 'linear-gradient(to right, var(--accent), var(--calc-bg-color))',
                    'gradient-9': 'radial-gradient(circle at 50% 0%, var(--accent), var(--calc-bg-color))',
                    'gradient-10': 'linear-gradient(120deg, var(--accent), var(--accent-bright))',
                    'gradient-11': 'linear-gradient(180deg, var(--calc-bg-color) 0%, var(--accent) 100%)',
                    'gradient-12': 'conic-gradient(from 0deg at 50% 50%, var(--calc-bg-color), var(--accent), var(--calc-bg-color))',
                    'gradient-13': 'linear-gradient(90deg, var(--accent), var(--accent-bright))',
                    'gradient-14': 'repeating-linear-gradient(45deg, var(--accent), var(--accent) 10px, var(--calc-bg-color) 10px, var(--calc-bg-color) 20px)',
                    'gradient-15': 'linear-gradient(to bottom right, var(--accent), var(--accent-tertiary))',
                    'gradient-16': 'linear-gradient(to top left, var(--accent-bright), var(--accent))',
                    'gradient-17': 'linear-gradient(135deg, var(--accent), white)',
                    'gradient-18': 'linear-gradient(225deg, var(--accent), black)',
                    'gradient-19': 'linear-gradient(to right, var(--accent), var(--accent-bright))',
                    'gradient-20': 'linear-gradient(to bottom, var(--accent), transparent)',
                    'gradient-21': 'linear-gradient(60deg, var(--accent), var(--accent-bright), var(--accent))',
                    'gradient-22': 'radial-gradient(circle, var(--accent) 0%, var(--calc-bg-color) 100%)',
                    'gradient-23': 'linear-gradient(135deg, transparent 0%, var(--accent) 100%), var(--calc-bg-color)',
                    'gradient-24': 'linear-gradient(to right, var(--accent), var(--accent-bright))',
                    'gradient-25': 'linear-gradient(45deg, var(--accent), var(--accent-tertiary), var(--accent))',
                    'gradient-subtle-3': 'linear-gradient(135deg, color-mix(in srgb, var(--accent) 5%, var(--calc-bg-color)), color-mix(in srgb, var(--accent-bright) 5%, var(--calc-bg-color)))',
                };

                if (gradients[this.dockStyle]) {
                    theme.bgImage = gradients[this.dockStyle];
                    // Specific tweaks
                    if (this.dockStyle === 'gradient-5' || this.dockStyle === 'gradient-5a' || this.dockStyle === 'gradient-5b' || this.dockStyle === 'gradient-5c' || this.dockStyle === 'gradient-5d' || this.dockStyle === 'gradient-5e' || this.dockStyle === 'gradient-5f') theme.shadow = '0 8px 32px rgba(100, 100, 255, 0.15)';
                    if (this.dockStyle === 'gradient-13') theme.bgOpacity = '0.8';
                    if (this.dockStyle === 'gradient-15' || this.dockStyle === 'gradient-16') theme.bgOpacity = '0.9';
                    if (this.dockStyle === 'gradient-17') theme.shadow = '0 4px 15px color-mix(in srgb, var(--accent) 40%, transparent)';
                    if (this.dockStyle === 'gradient-18') theme.color = 'white';
                    if (this.dockStyle === 'gradient-20') theme.border = '1px solid var(--accent)';
                    if (this.dockStyle === 'gradient-21') { theme.bgSize = '200% 200%'; theme.animation = 'gradientMove 5s ease infinite'; }
                }
            }

            // Border Logic
            let border = theme.border || 'none';
            if (this.borderStyle === 'subtle') border = '1px solid color-mix(in srgb, var(--calc-card-border) 20%, transparent)';
            else if (this.borderStyle === 'glass') border = '2px solid var(--glass-border-color)';
            else if (this.borderStyle === 'gradient') border = '2px solid transparent';

            let borderImage = 'none';
            if (this.borderStyle === 'gradient') {
                borderImage = 'linear-gradient(135deg, var(--accent), var(--accent-secondary, var(--accent))) 1';
            }

            return { theme, border, borderImage };
        },

        get dockThemeVars() {
            const { theme, border, borderImage } = this.getDockThemeConfig();

            // Construct CSS string with both variables AND properties
            let style = `
        --dock-bg: ${theme.bg};
        --dock-bg-opacity: ${theme.bgOpacity};
        --dock-bg-image: ${theme.bgImage || 'none'};
        --dock-backdrop: ${theme.backdrop};
        --dock-shadow: ${theme.shadow};
        --dock-radius: ${theme.radius};
        --dock-padding: ${theme.padding};
        --dock-border: ${border};
        --dock-border-image: ${borderImage};
        --dock-color: ${theme.color || 'inherit'};
        --dock-bg-size: ${theme.bgSize || 'auto'};
        --dock-animation: ${theme.animation || 'none'};
        background-color: ${theme.bg};
        background-image: ${theme.bgImage || 'none'};
        backdrop-filter: ${theme.backdrop};
        box-shadow: ${theme.shadow};
        border-radius: ${theme.radius};
        padding: ${theme.padding};
        border: ${border};
        border-image: ${borderImage};
        color: ${theme.color || 'inherit'};
        background-size: ${theme.bgSize || 'auto'};
        animation: ${theme.animation || 'none'};
      `;

            return style;
        },

        get mobileDockThemeVars() {
            const { theme } = this.getDockThemeConfig();
            return `
        background-color: ${theme.bg};
        backdrop-filter: ${theme.backdrop};
        color: ${theme.color || 'inherit'};
        --dock-bg: ${theme.bg};
        --dock-backdrop: ${theme.backdrop};
        --dock-color: ${theme.color || 'inherit'};
      `;
        },

        /**
         * Unified dock styling - combines theme vars with dock style classes/styles
         * Single source of truth for dock appearance
         */
        getDockStylesWithVars(dockStyle, borderStyle) {
            const vars = this.dockThemeVars;
            const uiStyles = uiClasses().getDockStyles(dockStyle, borderStyle);
            return {
                class: uiStyles.class,
                style: vars + '; ' + uiStyles.style
            };
        },

        init() {
            if (window.themeConfig?.showBottomNav) {
                getUIStore().bottomNavEnabled = true;
            }

            // When a calc is selected from list, switch from gallery to home view
            InstacalcEvents.on(InstacalcEvents.CALC_LIST_CLICK, () => {
                if (this.isGalleryLayout()) {
                    const homeView = window.Router?.VIEW?.HOME ?? 1;
                    Alpine?.store?.('view', homeView);
                    history.pushState({}, '', '/');
                }
            });

            InstacalcEvents.on(InstacalcEvents.MOBILE_CLOSE, () => {
                getUIStore().closeMobilePanel();
            });
            InstacalcEvents.on(InstacalcEvents.PANEL_OPEN, (e) => {
                if (e.detail?.panel) {
                    this.open(e.detail.panel, e.detail.force);
                }
            });
            InstacalcEvents.on(InstacalcEvents.LAYOUT_CHANGE, (e) => {
                const targetView = e.detail?.view;
                if (typeof targetView === 'undefined') return;
                const currentView = Alpine?.store('view');
                if (currentView !== targetView) {
                    Alpine?.store?.('view', targetView);
                }
            });

            // Listen for sidebar toggle from React component
            window.InstacalcEvents.on(window.InstacalcEvents.SIDEBAR_TOGGLE, () => {
                this.toggleLeftSidebar();
            });

            // Watch for dockStyle changes
            this.updateDockGradientVar();
            this.$watch('dockStyle', () => {
                this.updateDockGradientVar();
            });

            // Update dock gradient when gradient intensity changes
            window.addEventListener('gradient-intensity-changed', () => {
                this.updateDockGradientVar();
            });

            // Listen for dock position changes from ThemeSystem
            window.addEventListener('dock-position-changed', (e) => {
                if (e.detail?.fullPosition) {
                    this.panelPosition = e.detail.fullPosition;
                }
            });

            // React portals auto-detect tab changes via MutationObserver
        },

        // Helper methods
        get showPanel() {
            const store = getUIStore();
            const panel = store?.activePanel;
            const rightRailPanels = store?.rightRailPanels || ['theme', 'help', 'calcy', 'keypad', 'gallery', 'preview', 'debug', 'collab'];
            return panel !== null && rightRailPanels.includes(panel);
        },

        /**
         * Open or toggle a panel.
         * @param {string} name - Panel name
         * @param {boolean} force - If true, always open (never toggle). If false, toggle behavior.
         */
        open(name, force = false) {
            if (!name) return;
            if (force) {
                this.sidebarManager.openRight(name);
            } else {
                this.sidebarManager.toggleRight(name);
            }
        },

        collapse() {
            this.sidebarManager.closeRight();
        },

        closeAll() {
            this.sidebarManager.closeAll();
        },

        toggleBottomNavCollapse() {
            this.bottomNavAnimating = true;
            getUIStore().toggleBottomNavCollapse();
            const duration = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--toolbar-close-duration'));
            setTimeout(() => {
                this.bottomNavAnimating = false;
            }, duration);
        },
        bottomNavAnimating: false,

        // ============================================================
        // VIEW HELPERS - Layout predicates for UI decisions
        // ============================================================
        // Router.VIEW enum: HOME=1, SINGLE=3, LEGACY=5, GALLERY=6,
        //                   PUBLISHED=7, WIKI=8, COLLAB=9, URL_CALC=200
        //
        // Layout categories:
        // - App views (isHomeLayout, isGalleryLayout): Full UI, sidebar open
        // - Shared views (isSingleLayout): Single calc, sidebar collapsed
        // ============================================================

        currentView() {
            try {
                return Alpine?.store('view') ?? window.Router?.VIEW?.HOME ?? 1;
            } catch (_e) {
                return 1; // HOME
            }
        },

        // App home view - full editing mode with all features
        isHomeLayout() {
            const view = this.currentView();
            const HOME = window.Router?.VIEW?.HOME ?? 1;
            return view === HOME || view === -1 || view === 'home';
        },

        // Single calc view - shared/published/quick-link modes
        // Sidebar shown but collapsed by default
        isSingleLayout() {
            const view = this.currentView();
            const R = window.Router?.VIEW ?? {};
            return [R.SINGLE, R.LEGACY, R.PUBLISHED, R.WIKI, R.COLLAB, R.URL_CALC]
                .filter(v => v !== undefined)
                .includes(view);
        },

        // Gallery browsing view
        isGalleryLayout() {
            const view = this.currentView();
            return view === (window.Router?.VIEW?.GALLERY ?? 6);
        },

        // Published calc specifically (for CSS styling hooks)
        // Note: Also returns true for isSingleLayout() - they overlap intentionally
        isPublishedLayout() {
            const view = this.currentView();
            return view === (window.Router?.VIEW?.PUBLISHED ?? 7);
        },

        // App view = home or gallery (full editing UI with sidebar open)
        // Shared view = isSingleLayout (sidebar collapsed by default)
        isAppView() {
            return this.isHomeLayout() || this.isGalleryLayout();
        },

        // Helper for full layout check (Home, Gallery, Published)
        isFullLayout() {
            return this.isAppView() || this.isPublishedLayout();
        },

        showBottomNav() {
            const isDesktop = window.innerWidth >= 1024;
            return isDesktop && getUIStore().bottomNavEnabled && this.isHomeLayout();
        }
    };
}
// Panel opening now uses Alpine store directly

(function () {
    const panelToButton = {
        calcs: 'btn-calcs',
        calcy: 'btn-calcy',
        keypad: 'btn-keypad',
        help: 'btn-help',
        theme: 'btn-theme',
    };

    const triggerBounce = (elements) => {
        if (!elements || typeof elements.forEach !== 'function') return;
        elements.forEach((el) => {
            if (!el) return;
            el.classList.remove('panel-bounce-anim');
            void el.offsetWidth;
            el.classList.add('panel-bounce-anim');
            el.addEventListener('animationend', () => el.classList.remove('panel-bounce-anim'), { once: true });
        });
    };

    window.addEventListener('panel-bounce', (event) => {
        const panel = event?.detail?.panel;
        if (!panel) return;
        const btnId = panelToButton[panel];
        requestAnimationFrame(() => {
            if (btnId) {
                triggerBounce(document.querySelectorAll(`[data-id="${btnId}"]`));
            }
            const panelEls = document.querySelectorAll(`[data-panel="${panel}"]`);
            if (panelEls.length) {
                triggerBounce(panelEls);
            }
            const asideEl = document.querySelector('[data-panel-container]');
            if (asideEl) {
                triggerBounce([asideEl]);
            }
        });
    });
})();
// Alpine.js custom directives and components
document.addEventListener('alpine:init', () => {
    // Automatic tooltip positioning based on nav location
    Alpine.directive('tooltip-auto', (el) => {
        const navLocation = el.closest('[data-nav-location]')?.getAttribute('data-nav-location');
        const tooltipClass = navLocation === 'header' ? 'tooltip-bottom' : 'tooltip-top';
        el.classList.add(tooltipClass);
    });

    // Sidebar Accordion Component - My Calcs / Explore split
    Alpine.data('sidebarAccordion', () => ({
        myCalcsExpanded: true,
        exploreExpanded: true,
        myCalcsCount: 0,
        categoryCount: 8, // Default category count

        init() {
            // Load persisted state
            if (window.SettingsManager) {
                const state = window.SettingsManager.get('ui.sidebarAccordion');
                if (state) {
                    this.myCalcsExpanded = state.myCalcs ?? true;
                    this.exploreExpanded = state.explore ?? true;
                }
            } else {
                const saved = localStorage.getItem('sidebarAccordion');
                if (saved) {
                    try {
                        const state = JSON.parse(saved);
                        this.myCalcsExpanded = state.myCalcs ?? true;
                        this.exploreExpanded = state.explore ?? true;
                    } catch (e) {
                        // Ignore parse errors
                    }
                }
            }

            // Listen for calc count updates from React
            window.InstacalcEvents.on(window.InstacalcEvents.CALCS_UPDATED, (e) => {
                this.myCalcsCount = e.detail?.count || 0;
            });

            // Listen for gallery updates
            window.InstacalcEvents.on(window.InstacalcEvents.GALLERY_UPDATED, (e) => {
                const cats = e.detail?.categories;
                // Count categories with calcs (exclude empty and 'other' if empty)
                if (Array.isArray(cats)) {
                    this.categoryCount = cats.filter(c => c.count > 0 || c.slug !== 'other').length;
                }
            });

            // Initialize with current Calc count if available
            if (typeof Calc !== 'undefined' && Calc.all) {
                try {
                    this.myCalcsCount = Calc.all().length;
                } catch (e) {
                    // Calc not ready yet
                }
            }
        },

        toggleMyCalcs() {
            this.myCalcsExpanded = !this.myCalcsExpanded;
            this.saveState();
        },

        toggleExplore() {
            this.exploreExpanded = !this.exploreExpanded;
            this.saveState();
        },

        saveState() {
            const state = {
                myCalcs: this.myCalcsExpanded,
                explore: this.exploreExpanded
            };
            if (window.SettingsManager) {
                window.SettingsManager.set('ui.sidebarAccordion', state);
            } else {
                localStorage.setItem('sidebarAccordion', JSON.stringify(state));
            }
        }
    }));
});

// Sidebar Resizing Logic (Refactored from index.html)
function sidebarResizeData() {
    return {
        sidebarWidth: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width')) || 256,
        isResizing: false,
        startX: 0,
        startWidth: 0,
        minWidth: 192,  // 12rem
        maxWidth: 384,  // 24rem

        startResize(e) {
            this.isResizing = true;
            this.startX = e.clientX;
            this.startWidth = this.sidebarWidth;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },

        resize(e) {
            if (!this.isResizing) return;
            const delta = e.clientX - this.startX;
            const newWidth = Math.max(this.minWidth, Math.min(this.maxWidth, this.startWidth + delta));
            this.sidebarWidth = newWidth;
            document.documentElement.style.setProperty('--sidebar-width', `${newWidth}px`);
        },

        stopResize() {
            if (this.isResizing) {
                this.isResizing = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        }
    };
}
;
/**
 * Icons Module - Centralized SVG icons for Alpine templates
 * Single source of truth matching public/src/react/components/common/Icon.jsx
 *
 * Usage in Alpine templates:
 *   <span x-html="Icons.copy('w-4 h-4')"></span>
 *   <button x-html="Icons.get('copy', 'w-3 h-3')"></button>
 */
(function () {
  'use strict';

  // Default class if none provided
  const DEFAULT_CLASS = 'w-4 h-4';

  // SVG icons - matches Icon.jsx for consistency
  const ICONS = {
    // Copy icon (two overlapping rectangles)
    copy: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
      </svg>`,

    // Check/success icon
    check: (className = 'w-3 h-3') => `
      <svg class="${className}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12" />
      </svg>`,

    // External link icon
    externalLink: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
      </svg>`,

    // Book/docs icon
    book: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
      </svg>`,

    // Chevron down
    chevronDown: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
      </svg>`,

    // Refresh/reload icon
    refresh: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>`,

    // Terminal/debug icon
    terminal: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>`,

    // GitHub icon
    github: (className = DEFAULT_CLASS) => `
      <svg class="${className}" viewBox="0 0 24 24" fill="currentColor">
        <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
      </svg>`,

    // Heart (outline)
    heart: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
      </svg>`,

    // Heart (filled)
    heartFilled: (className = DEFAULT_CLASS) => `
      <svg class="${className}" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-2.688 6.86-4.989 9.007a25.18 25.18 0 01-4.244 3.17 15.247 15.247 0 01-.383.218l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
      </svg>`,

    // Plus icon
    plus: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
      </svg>`,

    // Close/X icon
    close: (className = 'w-3 h-3') => `
      <svg class="${className}" viewBox="0 0 8 8" fill="none">
        <path d="M0.258206 1.00652C0.351976 0.912791 0.479126 0.860131 0.611706 0.860131C0.744296 0.860131 0.871447 0.912791 0.965207 1.00652L3.61171 3.65302L6.25822 1.00652C6.30432 0.958771 6.35952 0.920671 6.42052 0.894471C6.48152 0.868271 6.54712 0.854471 6.61352 0.853901C6.67992 0.853321 6.74572 0.865971 6.80722 0.891111C6.86862 0.916251 6.92442 0.953381 6.97142 1.00032C7.01832 1.04727 7.05552 1.1031 7.08062 1.16454C7.10572 1.22599 7.11842 1.29183 7.11782 1.35822C7.11722 1.42461 7.10342 1.49022 7.07722 1.55122C7.05102 1.61222 7.01292 1.6674 6.96522 1.71352L4.31871 4.36002L6.96522 7.00648C7.05632 7.10078 7.10672 7.22708 7.10552 7.35818C7.10442 7.48928 7.05182 7.61468 6.95912 7.70738C6.86642 7.80018 6.74102 7.85268 6.60992 7.85388C6.47882 7.85498 6.35252 7.80458 6.25822 7.71348L3.61171 5.06702L0.965207 7.71348C0.870907 7.80458 0.744606 7.85498 0.613506 7.85388C0.482406 7.85268 0.357007 7.80018 0.264297 7.70738C0.171597 7.61468 0.119017 7.48928 0.117877 7.35818C0.116737 7.22708 0.167126 7.10078 0.258206 7.00648L2.90471 4.36002L0.258206 1.71352C0.164476 1.61976 0.111816 1.4926 0.111816 1.36002C0.111816 1.22744 0.164476 1.10028 0.258206 1.00652Z" fill="currentColor"></path>
      </svg>`,

    // Search/magnifying glass
    search: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>`,

    // Info circle
    info: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
      </svg>`,

    // Trash/delete
    trash: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
      </svg>`,

    // Pencil/edit
    pencil: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
      </svg>`,

    // Link
    link: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>`,

    // Eye (visible)
    eye: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>`,

    // Eye off (hidden)
    eyeOff: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
      </svg>`,

    // Folder
    folder: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>`,

    // Download
    download: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
      </svg>`,

    // Upload
    upload: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
      </svg>`,

    // Send/Arrow Up (for chat send button)
    send: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>`,

    // Settings/gear
    settings: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 2.25h4.5l.452 2.713a6.75 6.75 0 0 1 2.335 1.348l2.532-.844 2.25 3.897-2.08 1.52a6.778 6.778 0 0 1 0 2.634l2.08 1.52-2.25 3.897-2.532-.844a6.75 6.75 0 0 1-2.335 1.348l-.452 2.713h-4.5l-.452-2.713a6.75 6.75 0 0 1-2.335-1.348l-2.532.844-2.25-3.897 2.08-1.52a6.778 6.778 0 0 1 0-2.634l-2.08-1.52 2.25-3.897 2.532.844a6.75 6.75 0 0 1 2.335-1.348l.452-2.713z" />
        <circle cx="12" cy="12" r="2.5" />
      </svg>`,

    // Globe
    globe: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,

    // Lock (closed)
    lock: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>`,

    // Lock open
    lockOpen: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>`,

    // Share
    share: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
      </svg>`,

    // Keypad (calculator-like grid)
    keypad: (className = DEFAULT_CLASS) => `
      <svg class="${className}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="1.3574711" y="3.2405481" width="19.771212" height="19.438904" rx="4.9428029" fill="none" stroke="currentColor" stroke-width="1.47033" />
        <g id="g4" transform="matrix(1.1208805,0,0,1.1478475,-2.1594742,-0.63410138)">
          <g id="g5" transform="translate(0.3966196,0.6588002)">
            <g transform="matrix(-0.7241189,0.70710678,-0.70710678,-0.69049434,11.921202,7.9937231)" id="g1">
              <path d="M 1.53,0.47 C 1.24,0.18 0.76,0.18 0.47,0.47 0.18,0.76 0.18,1.24 0.47,1.53 L 1.41,2.47 0.47,3.41 c -0.29,0.29 -0.29,0.77 0,1.06 0.29,0.29 0.77,0.29 1.06,0 L 2.47,3.53 3.41,4.47 c 0.29,0.29 0.77,0.29 1.06,0 0.29,-0.29 0.29,-0.77 0,-1.06 L 3.53,2.47 4.47,1.53 C 4.76,1.24 4.76,0.76 4.47,0.47 4.18,0.18 3.7,0.18 3.41,0.47 L 2.47,1.41 Z" fill="currentColor" />
            </g>
            <rect x="12.597517" y="7.2847557" width="5" height="1.5" rx="0.75" fill="currentColor" />
            <g transform="rotate(90,-0.56345969,11.419534)" id="g2">
              <path d="M 1.53,0.47 C 1.24,0.18 0.76,0.18 0.47,0.47 0.18,0.76 0.18,1.24 0.47,1.53 L 1.41,2.47 0.47,3.41 c -0.29,0.29 -0.29,0.77 0,1.06 0.29,0.29 0.77,0.29 1.06,0 L 2.47,3.53 3.41,4.47 c 0.29,0.29 0.77,0.29 1.06,0 0.29,-0.29 0.29,-0.77 0,-1.06 L 3.53,2.47 4.47,1.53 C 4.76,1.24 4.76,0.76 4.47,0.47 4.18,0.18 3.7,0.18 3.41,0.47 L 2.47,1.41 Z" fill="currentColor" />
            </g>
            <g fill="currentColor" id="g3" transform="translate(-1.4024828,-1.4731604)">
              <circle cx="16.5" cy="14" r="0.75" />
              <rect x="14" y="15.176154" width="5" height="1.5" rx="0.75" />
              <circle cx="16.5" cy="17.852308" r="0.75" />
            </g>
          </g>
        </g>
      </svg>`,

    // Calcy (robot/AI icon)
    calcy: (className = DEFAULT_CLASS) => `
      <div class="calcy-icon ${className}">
        <img src="/img/calcy/calcy.webp" alt="Calcy AI" class="calcy-icon__image" />
        <div class="calcy-icon-overlay"></div>
      </div>`,

    // Help (question mark circle)
    help: (className = DEFAULT_CLASS) => `
      <svg xmlns="http://www.w3.org/2000/svg" class="${className}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
      </svg>`,

    // Theme (sun/display)
    theme: (className = DEFAULT_CLASS) => `
      <svg class="${className}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>`,

    // Beaker/Flask (labs/experimental)
    beaker: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23-.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>`,

    // Flask alias for beaker
    flask: (className = DEFAULT_CLASS) => ICONS.beaker(className),

    // Rectangle stack (layered presets/configurations)
    rectangleStack: (className = DEFAULT_CLASS) => `
      <svg class="${className}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122" />
      </svg>`,
  };

  /**
   * Get an icon by name
   * @param {string} name - Icon name (e.g., 'copy', 'check')
   * @param {string} [className] - CSS classes for the SVG
   * @returns {string} SVG HTML string
   */
  function get(name, className) {
    const iconFn = ICONS[name];
    if (!iconFn) {
      console.warn(`Icon not found: ${name}`);
      return '';
    }
    return iconFn(className);
  }

  // Create callable functions for each icon
  const Icons = { get };
  Object.keys(ICONS).forEach(name => {
    Icons[name] = ICONS[name];
  });

  // Also include category icons if loaded
  if (window.CategoryIcons) {
    Icons.category = window.CategoryIcons;
  }

  // Export
  window.Icons = Icons;
})();
;
/**
 * Category Icons Module
 * Centralized SVG icons for gallery categories.
 * Used by gallery modal and sidebar templates.
 */
(function() {
  'use strict';

  // SVG icon paths keyed by category slug
  const CATEGORY_ICONS = {
    finance: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.467-.22-2.121-.659-1.172-.879-1.172-2.303 0-3.182s3.07-.879 4.242 0L15 9m-3 0V6" />
    </svg>`,

    health: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>`,

    cooking: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.871c1.355 0 2.697.056 4.024.166C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75-1.5.75a3.354 3.354 0 0 1-3 0 3.354 3.354 0 0 0-3 0 3.354 3.354 0 0 1-3 0 3.354 3.354 0 0 0-3 0 3.354 3.354 0 0 1-3 0L3 16.5m15-3.379a48.474 48.474 0 0 0-6-.371c-2.032 0-4.034.126-6 .371m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.169c0 .621-.504 1.125-1.125 1.125H4.125A1.125 1.125 0 0 1 3 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 0 1 6 13.12" />
    </svg>`,

    math: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" />
    </svg>`,

    science: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23-.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
    </svg>`,

    conversion: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>`,

    gaming: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 0 1-.657.643 48.39 48.39 0 0 1-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 0 1-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 0 0-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 0 1-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 0 0 .657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 0 1-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 0 0 5.427-.63 48.05 48.05 0 0 0 .582-4.717.532.532 0 0 0-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 0 0 .658-.663 48.422 48.422 0 0 0-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 0 1-.61-.58v0Z" />
    </svg>`,

    other: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
    </svg>`
  };

  // Default fallback icon
  const DEFAULT_ICON = CATEGORY_ICONS.math;

  /**
   * Get SVG icon HTML for a category
   * @param {string} category - Category slug (finance, health, etc.)
   * @param {string} [className] - Optional class override
   * @returns {string} SVG HTML string
   */
  function getIcon(category, className) {
    let svg = CATEGORY_ICONS[category] || DEFAULT_ICON;
    if (className) {
      svg = svg.replace(/class="[^"]*"/, `class="${className}"`);
    }
    return svg;
  }

  /**
   * Get category icon for a calc based on its category
   * @param {Object} calc - Calculator object with category property
   * @returns {string} SVG HTML string
   */
  function getCalcIcon(calc) {
    return getIcon(calc?.category || 'other');
  }

  // Export module
  window.CategoryIcons = {
    ICONS: CATEGORY_ICONS,
    getIcon,
    getCalcIcon
  };
})();
;
(function () {
  if (window.InstacalcInfoFab) return;

  window.InstacalcInfoFab = function (variant = 'standalone') {
    const variants = {
      standalone: {
        wrapperClass: 'fixed bottom-5 right-4 z-[70] pointer-events-none sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-10',
        innerWrapperClass: 'flex flex-col items-end gap-3 pointer-events-auto',
        cardPositionClass: 'origin-bottom-right',
        buttonClass: 'relative flex items-center justify-center w-12 h-12 text-theme-text opacity-50 hover:opacity-100 hover:scale-[1.02] transition-all',
        badgePositionClass: 'top-1.5 right-1.5',
        buttonVariant: 'standalone',
        buttonTooltip: null,
        dialogId: 'info-hub-card',
        iconWrapperClass: 'rounded-full w-9 h-9',
        iconWrapperStyle: 'background: color-mix(in srgb, var(--accent) 18%, transparent);'
      },
      nav: {
        wrapperClass: 'fixed bottom-14 left-4 z-[80] pointer-events-none', // Position near sidebar bottom-left
        innerWrapperClass: 'contents',
        cardPositionClass: 'pointer-events-auto', // Card itself takes pointer events
        buttonClass: 'hidden',
        badgePositionClass: 'top-0.5 right-0.5',
        buttonVariant: 'nav',
        buttonTooltip: 'More...',
        dialogId: 'info-hub-card-nav',
        iconWrapperClass: '',
        iconWrapperStyle: null
      }
    };

    const config = variants[variant] || variants.standalone;

    return {
      variant,
      wrapperClass: config.wrapperClass,
      innerWrapperClass: config.innerWrapperClass,
      cardPositionClass: config.cardPositionClass,
      buttonClass: config.buttonClass,
      badgePositionClass: config.badgePositionClass,
      buttonVariant: config.buttonVariant,
      buttonTooltip: config.buttonTooltip,
      dialogId: config.dialogId,
      iconWrapperClass: config.iconWrapperClass,
      iconWrapperStyle: config.iconWrapperStyle,
      open: false,
      showBadge: false,
      storageKey: 'instacalc:fabhub:open',
      releaseKey: 'instacalc:fabhub:lastSeen',
      releaseTag: '2024-11-15',
      quickFeedback: '',
      quickEmail: '',
      feedbackState: 'idle', // idle | submitting | success | error
      feedbackError: '',
      showFeedbackDetails: false,
      shareLinkText: 'Copy link',
      getShareableLink() {
        const url = window.location.href;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => {
            this.shareLinkText = 'Copied!';
            setTimeout(() => {
              this.shareLinkText = 'Copy link';
            }, 2000);
          }).catch(err => {
            console.error('Failed to copy: ', err);
            this.shareLinkText = 'Error';
          });
        }
      },
      init() {
        // Guard against double initialization (race between Alpine MutationObserver and loadHTML)
        if (this._initialized) {
          if (window.Debug) window.Debug.log('ui', 'FAB already initialized, skipping');
          return;
        }
        this._initialized = true;

        if (window.Debug) window.Debug.log('ui', 'Initializing with variant:', this.variant);
        try {
          this.open = localStorage.getItem(this.storageKey) === '1';
          this.showBadge = localStorage.getItem(this.releaseKey) !== this.releaseTag;
        } catch (e) {
          this.open = false;
          this.showBadge = true;
        }

        // Listen for FAB_TOGGLE events from the dock menu button
        if (window.Debug) window.Debug.log('ui', 'Setting up FAB_TOGGLE event listener on window');
        this.cleanupToggle = window.InstacalcEvents.on(window.InstacalcEvents.FAB_TOGGLE, (e) => {
          e.preventDefault();
          e.stopImmediatePropagation(); // Prevent double-toggling
          if (window.Debug) window.Debug.log('ui', 'Received FAB_TOGGLE event', e);
          this.toggle();
        });

        this.$watch('open', (value) => {
          if (window.Debug) window.Debug.log('ui', 'Open state changed to:', value);
          if (value) {
            this.markSeen();
            this.$nextTick(() => {
              this.$refs.firstAction?.focus();
            });
          }
          this.persist();
        });
      },
      destroy() {
        if (window.Debug) window.Debug.log('ui', 'Destroying component, cleaning up listeners');
        if (this.cleanupToggle) {
          this.cleanupToggle();
        }
      },
      persist() {
        try {
          localStorage.setItem(this.storageKey, this.open ? '1' : '0');
        } catch (e) {
          // ignore storage issues
        }
      },
      markSeen() {
        this.showBadge = false;
        try {
          localStorage.setItem(this.releaseKey, this.releaseTag);
        } catch (e) {
          // ignore storage issues
        }
      },
      toggle(force) {
        if (window.Debug) window.Debug.log('ui', 'Toggle called, current state:', this.open, 'force:', force);
        if (typeof force === 'boolean') {
          this.open = force;
        } else {
          this.open = !this.open;
        }
        if (!this.open) {
          this.feedbackState = 'idle';
          this.feedbackError = '';
        }
      },
      close() {
        this.open = false;
        this.feedbackState = 'idle';
        this.feedbackError = '';
      },
      navigatePanel(panel) {
        if (panel === 'tour') {
          window.InstacalcEvents.dispatch(window.InstacalcEvents.TOUR_OPEN, { source: 'menu' });
          this.close();
          return;
        }
        if (window.Alpine?.store) {
          window.Alpine.store('ui').open(panel);
        }
        this.close();
      },
      openWindow(url) {
        window.open(url, '_blank', 'noopener');
        this.close();
      },
      openTour() {
        this.close();
        // Open help panel (force=true to always open, never toggle)
        if (typeof Alpine !== 'undefined' && Alpine.store('ui')) {
          Alpine.store('ui').open('help', true);
        }
        // Dispatch TOUR_OPEN to switch to tour tab
        // Small delay to ensure help panel template has loaded
        setTimeout(() => {
          window.InstacalcEvents.dispatch(window.InstacalcEvents.TOUR_OPEN, { source: 'menu' });
        }, 50);
      },
      async submitQuickFeedback() {
        if (!this.quickFeedback.trim()) {
          this.feedbackError = 'Please share a quick note.';
          return;
        }

        this.feedbackState = 'submitting';
        this.feedbackError = '';

        try {
          const formData = new FormData();
          formData.append('entry.631180420', this.quickFeedback.trim());
          if (this.quickEmail.trim()) {
            formData.append('entry.2145236286', this.quickEmail.trim());
          }

          await fetch('https://docs.google.com/forms/d/e/1FAIpQLScVyG_mjPRzEUoQN_4aBhAT5Q5o2PaQwgL7IlwgqL56275ohw/formResponse', {
            method: 'POST',
            mode: 'no-cors',
            body: formData
          });

          this.feedbackState = 'success';
          this.quickFeedback = '';
          this.quickEmail = '';
          this.showFeedbackDetails = false;

          setTimeout(() => {
            if (this.feedbackState === 'success') {
              this.feedbackState = 'idle';
            }
          }, 4000);
        } catch (error) {
          console.error('[FAB] Feedback submit failed', error);
          this.feedbackState = 'error';
          this.feedbackError = 'Unable to send feedback right now.';
        }
      }
    };
  };
})();
;
(function () {
  if (window.sharedIntroData) return;

  window.sharedIntroData = function () {
    return {
      open: false,
      typedText: '',
      showResult: false,
      currentResult: '',
      currentExample: null,
      typingInterval: null,
      cycleTimeout: null,
      examples: [
        { input: '250k + 10%', result: '275,000' },
        { input: '5 miles in km', result: '8.05 km' },
        { input: 'today + 90 days', result: 'Feb 27, 2026' }
      ],

      init() {
        // Start demo when panel opens
        this.$watch('open', (val) => {
          if (val) {
            this.startDemo();
          } else {
            this.stopDemo();
          }
        });
      },

      startDemo() {
        this.demoExample(this.examples[0]);
      },

      stopDemo() {
        if (this.typingInterval) {
          clearInterval(this.typingInterval);
          this.typingInterval = null;
        }
        if (this.cycleTimeout) {
          clearTimeout(this.cycleTimeout);
          this.cycleTimeout = null;
        }
        this.typedText = '';
        this.showResult = false;
        this.currentExample = null;
      },

      demoExample(ex) {
        // Clear any existing intervals/timeouts
        if (this.typingInterval) {
          clearInterval(this.typingInterval);
        }
        if (this.cycleTimeout) {
          clearTimeout(this.cycleTimeout);
        }

        this.currentExample = ex;
        this.showResult = false;
        this.typedText = '';

        let i = 0;
        const chars = ex.input.split('');

        this.typingInterval = setInterval(() => {
          if (i < chars.length) {
            this.typedText = chars.slice(0, i + 1).join('');
            i++;
          } else {
            clearInterval(this.typingInterval);
            this.typingInterval = null;
            this.currentResult = ex.result;
            this.showResult = true;

            // Auto-advance to next example after 3s
            this.cycleTimeout = setTimeout(() => {
              if (this.open) {
                const currentIdx = this.examples.findIndex(e => e.input === ex.input);
                const nextIdx = (currentIdx + 1) % this.examples.length;
                this.demoExample(this.examples[nextIdx]);
              }
            }, 3000);
          }
        }, 80);
      }
    };
  };
})();
;
(function () {
  if (window.InstacalcTour) return;

  const TOUR_STORAGE_KEY = 'instacalc:tour:dismissed';

  const steps = [
    {
      id: 'hero',
      title: 'The calculator that works how you think.',
      description: 'Type naturally, mix text with math, and get instant answers without wrestling with spreadsheets or apps.',
      highlights: ['Natural language like “250k + 10%”', 'Variables, dates, currencies, units', 'Share anywhere—no login required'],
      media: {
        label: 'Happier calculations',
        metric: '“Loving a calculator just might be possible.”'
      },
      ctaLabel: 'Load hero calc',
      calc: {
        id: 'hero-demo',
        url: 'https://instacalc.com/calcs/what-if-demo'
      }
    },
    {
      id: 'quickstart',
      title: 'Quickstart-ready controls',
      description: 'Use shortcuts like shift+enter, drag-to-duplicate and inline conversions. Everything from quickstart lives here—no setup.',
      highlights: ['Instant answers, zero config', 'Keyboard + touch gestures you already know', 'Save/clone any calc in seconds'],
      media: {
        label: 'Quickstart essentials',
        metric: 'Natural input → instant output'
      },
      ctaLabel: 'Open quickstart calc',
      calc: {
        id: 'quickstart-demo',
        url: 'https://instacalc.com/calcs/quickstart-demo'
      }
    },
    {
      id: 'features',
      title: 'Ridiculously handy features',
      description: 'Unit conversions, Goalseek, programming, even AI-powered Calcy. Everything from the features section, inside one panel.',
      highlights: ['Convert anything inline', 'Goalseek + variables + programming', 'Chart + AI helper baked in'],
      media: {
        label: 'Feature arsenal',
        metric: 'Teams replace spreadsheet tabs with Instacalc'
      },
      ctaLabel: 'See feature pack',
      calc: {
        id: 'feature-pack',
        url: 'https://instacalc.com/calcs/feature-pack'
      }
    },
    {
      id: 'share',
      title: 'Shareable mini calculators.',
      description: 'Every calc is a live web page. Embed it in a wiki, paste into chat, or drop a link in email—readers can explore safely.',
      highlights: ['Instant embeds + forum-ready links', 'Cloneable templates for your team', 'Disqus + feedback baked in'],
      media: {
        label: 'Community-approved',
        metric: 'Made for docs, wikis, and support teams'
      },
      ctaLabel: 'Share a template',
      calc: {
        id: 'shipping-estimator',
        url: 'https://instacalc.com/calcs/shipping-estimator'
      }
    }
  ];

  window.InstacalcTour = function () {
    return {
      steps,
      index: 0,
      open: false,
      dismissed: false,
      init() {
        this.dismissed = localStorage.getItem(TOUR_STORAGE_KEY) === '1';

        window.InstacalcEvents.on(window.InstacalcEvents.TOUR_TOGGLE, () => {
          this.open = !this.open;
          if (!this.open) this.persistDismiss();
        });

        window.InstacalcEvents.on(window.InstacalcEvents.TOUR_OPEN, (event) => {
          this.open = true;
          const shouldScrollToTour = event?.detail?.source === 'menu';
          if (shouldScrollToTour) {
            // Scroll to tour after it becomes visible
            this.$nextTick(() => {
              this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }
          // Load nested content after DOM updates
          setTimeout(() => {
            if (typeof window.loadHTML === 'function') {
              window.loadHTML();
              // Retry to catch deeply nested loads
              setTimeout(() => window.loadHTML(), 100);
              setTimeout(() => window.loadHTML(), 300);

              // Call scanForEmbed after content loads to render gallery calc
              if (window.Action?.scanForEmbed) {
                setTimeout(() => window.Action.scanForEmbed(), 150);
                setTimeout(() => window.Action.scanForEmbed(), 400);
                setTimeout(() => window.Action.scanForEmbed(), 700);
              }
            }
          }, 50);
        });

        // Auto-open disabled - tour is available via Help panel or FAB menu
        // if (!this.dismissed) {
        //   this.$nextTick(() => {
        //     window.InstacalcEvents.dispatch(window.InstacalcEvents.TOUR_OPEN, { source: 'auto' });
        //   });
        // }
      },
      persistDismiss() {
        try {
          localStorage.setItem(TOUR_STORAGE_KEY, '1');
          this.dismissed = true;
        } catch (e) {
          /* ignore */
        }
      },
      resetDismiss() {
        try {
          localStorage.removeItem(TOUR_STORAGE_KEY);
        } catch (e) { }
        this.dismissed = false;
      },
      select(i) {
        if (i >= 0 && i < this.steps.length) {
          this.index = i;
        }
      },
      next() {
        this.index = (this.index + 1) % this.steps.length;
      },
      prev() {
        this.index = (this.index - 1 + this.steps.length) % this.steps.length;
      },
      activeStep() {
        return this.steps[this.index];
      },
      cta(step = this.activeStep()) {
        if (!step) return;
        window.InstacalcEvents.dispatch(window.InstacalcEvents.TOUR_LAUNCH_CALC, { step });
        const targetUrl = step.ctaUrl || step.calc?.url;
        if (targetUrl) {
          window.open(targetUrl, '_blank', 'noopener');
        }
      }
    };
  };
})();
;
(function () {
  if (window.tour1Data) return;

  const TOUR_STORAGE_KEY = 'instacalc:tour1:dismissed';

  window.tour1Data = function () {
    return {
      open: true, // Always open when not dismissed
      dismissed: false,
      activeTab: 'quickstart',

      init() {
        // Check if previously dismissed
        try {
          this.dismissed = localStorage.getItem(TOUR_STORAGE_KEY) === '1';
        } catch (e) {
          this.dismissed = false;
        }

        // Auto-scroll removed on init

        // Listen for tour open events
        window.InstacalcEvents.on(window.InstacalcEvents.TOUR_OPEN, () => {
          this.dismissed = false;
          try {
            localStorage.removeItem(TOUR_STORAGE_KEY);
          } catch (e) {
            // ignore
          }
          this.scrollToView();
        });
      },

      // Keyboard nav for tabs: ArrowLeft/Right/Home/End
      onTabKey(e) {
        try {
          const tabs = Array.from(document.querySelectorAll('#tour-panel [role="tab"]'));
          if (!tabs.length) return;

          // Which tab is the current target? If the container had focus, fall back to activeTab
          let idx = tabs.indexOf(e.target);
          if (idx === -1) idx = tabs.findIndex((t) => t.dataset && t.dataset.tab === this.activeTab);
          if (idx === -1) return;

          let next = idx;
          if (e.key === 'ArrowRight' || e.key === 'Right') next = (idx + 1) % tabs.length;
          else if (e.key === 'ArrowLeft' || e.key === 'Left') next = (idx - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = tabs.length - 1;
          else return; // not a navigation key

          const nextTab = tabs[next];
          if (nextTab) {
            this.activeTab = nextTab.dataset.tab || this.activeTab;
            // focus the newly selected tab after DOM updates
            setTimeout(() => {
              try { nextTab.focus(); } catch (e) { /* ignore */ }
            }, 0);
            e.preventDefault();
          }
        } catch (err) {
          // be defensive in tests / older engines
        }
      },

      // Removed toggle() function

      scrollToView() {
        // Scroll into view after expansion
        setTimeout(() => {
          const el = document.getElementById('tour-panel');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      },

      close() {
        this.persistDismiss();
      },

      persistDismiss() {
        try {
          localStorage.setItem(TOUR_STORAGE_KEY, '1');
          this.dismissed = true;
        } catch (e) {
          // ignore storage issues
        }
      },

      // Helper to get tab button classes (reduces duplication)
      getTabClass(tabName) {
        const base = 'pb-1 font-medium transition-colors border-b-2 whitespace-nowrap';
        const conditional = this.activeTab === tabName ? 'border-theme-accent text-theme-text' : 'border-transparent text-theme-text-secondary hover:text-theme-text';
        return `${base} ${conditional}`;
      },

      // Helper to get panel attributes (reduces duplication)
      getPanelAttrs(tabName) {
        return {
          'role': 'tabpanel',
          'id': `tour-panel-${tabName}`,
          'aria-labelledby': `tour-tab-${tabName}`,
          'tabindex': '0'
        };
      }
    };
  };
})();
;
/**
 * Tour Welcome Button - Floating button for new users
 *
 * Shows a small floating button (bottom-right) that:
 * - Displays "New to instacalc?" with "Take a quick tour" link
 * - Has X button to dismiss
 * - Auto-dismisses when user clicks to take the tour
 * - Persists dismissal state in localStorage
 *
 * Opens the help panel with Tour tab when clicked.
 */

const TOUR_WELCOME_STORAGE_KEY = 'instacalc:tour-welcome:dismissed';

window.tourWelcomeData = function() {
  return {
    dismissed: false,

    init() {
      // Check if previously dismissed
      try {
        this.dismissed = localStorage.getItem(TOUR_WELCOME_STORAGE_KEY) === '1';
      } catch (e) {
        this.dismissed = false;
      }
    },

    /**
     * Dismiss the welcome button (user clicked X)
     */
    dismiss() {
      try {
        localStorage.setItem(TOUR_WELCOME_STORAGE_KEY, '1');
      } catch (e) {
        // Ignore storage errors (private browsing, etc.)
      }
      this.dismissed = true;
    },

    /**
     * Open the tour in the help sidebar
     */
    openTour() {
      // Auto-dismiss the welcome button
      this.dismiss();

      // Open help panel (force=true to always open, never toggle)
      if (typeof Alpine !== 'undefined' && Alpine.store('ui')) {
        Alpine.store('ui').open('help', true);
      }

      // Signal to help panel to switch to tour tab
      // Small delay to ensure help-panel template has loaded
      setTimeout(() => {
        window.InstacalcEvents.dispatch(window.InstacalcEvents.HELP_OPEN_TOUR, { collapseLeft: true });
      }, 50);
    },

    /**
     * Reset dismissal (for testing/debugging)
     */
    resetDismiss() {
      try {
        localStorage.removeItem(TOUR_WELCOME_STORAGE_KEY);
      } catch (e) {
        // Ignore
      }
      this.dismissed = false;
    }
  };
};
;
/**
 * Preview Panel - Alpine data for the Present mode preview panel
 *
 * The actual rendering is done by React's PreviewPanelPortal component,
 * which portals PresentModeRows into #preview-panel-mount when active.
 *
 * This Alpine component just provides the container.
 * React's PreviewPanelPortal auto-detects via MutationObserver.
 */

// Defensive semicolon for concatenation safety
;

(function() {
    if (window.Debug) window.Debug.log('ui', 'Initializing previewPanelData...');

    window.previewPanelData = function () {
        return {
            init() {
                if (window.Debug) window.Debug.log('ui', 'Alpine component initialized');
                // React's PreviewPanelPortal auto-detects panel via MutationObserver
            }
        };
    };
})();;
/**
 * Help Panel - Tabbed container for Tour and Reference content
 *
 * Tabs:
 * - Tour: Interactive tour/onboarding content
 * - Reference: Formula reference tables
 *
 * Events:
 * - InstacalcEvents.HELP_OPEN_TOUR: Switch to tour tab (fired by tour-welcome button)
 */

window.helpPanelData = function () {
  return {
    activeTab: 'reference',  // Default to reference tab

    init() {
      // Listen for tour open events to switch to tour tab
      window.InstacalcEvents.on(window.InstacalcEvents.HELP_OPEN_TOUR, () => {
        this.activeTab = 'tour';
      });

      // Listen for TOUR_OPEN (from FAB menu, etc.) to switch to tour tab
      window.InstacalcEvents.on(window.InstacalcEvents.TOUR_OPEN, () => {
        this.activeTab = 'tour';
      });
    },

    /**
     * Get dynamic classes for tab buttons
     */
    getTabClass(tabName) {
      const base = 'px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-150';
      const active = 'bg-[var(--accent)] text-white';
      const inactive = 'text-theme-text-secondary hover:text-theme-text hover:bg-[var(--bg-secondary-mix-40)]';

      return `${base} ${this.activeTab === tabName ? active : inactive}`;
    }
  };
};
;
// VoterIdService.js
// Generates consistent voter IDs for roadmap voting
// Uses Firebase UID if signed in, localStorage fingerprint if anonymous

const STORAGE_KEY = 'instacalc_voter_id';

const VoterIdService = {
  getVoterId() {
    // Signed-in user: use Firebase UID
    const user = window.firebase?.auth?.()?.currentUser;
    if (user && !user.isAnonymous) {
      return `uid_${user.uid}`;
    }

    // Anonymous: use localStorage fingerprint
    let fingerprintId = localStorage.getItem(STORAGE_KEY);
    if (!fingerprintId) {
      fingerprintId = this._generateFingerprint();
      localStorage.setItem(STORAGE_KEY, fingerprintId);
    }
    return `anon_${fingerprintId}`;
  },

  _generateFingerprint() {
    const random = Math.random().toString(36).substring(2, 15);
    const time = Date.now().toString(36);
    return `${random}_${time}`;
  }
};

window.VoterIdService = VoterIdService;
;
// RoadmapVoteService.js
// Client-side service for roadmap voting via Cloud Functions

const RoadmapVoteService = {
  _apiBase: '/api/roadmap',

  async toggleVote(itemSlug) {
    const voterId = window.VoterIdService.getVoterId();

    try {
      const response = await fetch(`${this._apiBase}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemSlug, voterId })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Vote failed');
      }

      const result = await response.json();
      return {
        hasVoted: result.hasVoted,
        count: result.count
      };
    } catch (e) {
      console.error('Vote error:', e);
      return { hasVoted: false, count: 0 };
    }
  },

  async getVotes() {
    try {
      const response = await fetch(`${this._apiBase}/votes`);

      if (!response.ok) {
        // Silently return null - voting will be disabled
        return null;
      }

      // Check content type to avoid parsing HTML as JSON (happens in local dev)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // API not available (likely local dev) - silently disable voting
        return null;
      }

      const result = await response.json();
      return result.votes || {};
    } catch (e) {
      // Silently return null - voting will be disabled
      return null;
    }
  },

  hasVoted(voteData, itemSlug) {
    if (!voteData || !voteData[itemSlug]) return false;
    const voterId = window.VoterIdService.getVoterId();
    return voteData[itemSlug].voters?.[voterId] || false;
  },

  async submitSuggestion(text) {
    const voterId = window.VoterIdService.getVoterId();

    try {
      const response = await fetch(`${this._apiBase}/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voterId })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Suggestion failed');
      }

      return true;
    } catch (e) {
      console.error('Suggestion error:', e);
      throw e;
    }
  }
};

window.RoadmapVoteService = RoadmapVoteService;
;
// roadmap-voting.js
// Alpine component for interactive roadmap with voting

// Counter for unique instance IDs (prevents key collisions when template loads twice)
let roadmapInstanceCounter = 0;

document.addEventListener('alpine:init', () => {
  Alpine.data('roadmapData', () => ({
    // Unique ID per instance (for x-for :key attributes)
    instanceId: 'roadmap' + (++roadmapInstanceCounter),
    loading: true,
    error: false,
    tagline: '',
    sections: [],
    voteData: {},
    votingEnabled: true,
    voting: null,
    suggestionText: '',
    submitting: false,
    submitted: false,

    async init() {
      try {
        const response = await fetch('/roadmap.md');
        if (!response.ok) throw new Error('Failed to load');
        const md = await response.text();
        this.parseRoadmap(md);

        if (window.RoadmapVoteService) {
          const votes = await window.RoadmapVoteService.getVotes();
          if (votes === null) {
            this.votingEnabled = false;
            this.voteData = {};
          } else {
            this.voteData = votes;
            this.votingEnabled = true;
          }
        }

        this.loading = false;
      } catch (_e) {
        this.error = true;
        this.loading = false;
      }
    },

    parseRoadmap(markdown) {
      this.sections = []; // Reset sections to avoid duplicates
      const lines = markdown.split('\n');
      let currentSection = null;

      for (const line of lines) {
        if (line.startsWith('# ')) continue;

        if (!this.tagline && line.trim() && !line.startsWith('#') && !line.startsWith('-')) {
          this.tagline = line.trim();
          continue;
        }

        if (line.startsWith('### ')) {
          const title = line.replace(/^###\s*\d+\.\s*/, '').trim();
          currentSection = { title, items: [] };
          this.sections.push(currentSection);
          continue;
        }

        if (line.startsWith('- [') && currentSection) {
          const done = line.startsWith('- [x]');
          const content = line.replace(/^- \[.\]\s*/, '');
          const match = content.match(/\*\*(.+?)\*\*\s*(?:\[(\w+)\])?\s*(?:-\s*(.+))?/);
          if (match) {
            currentSection.items.push({
              name: match[1],
              badge: match[2] || null,  // e.g., "beta"
              description: match[3] || '',
              done,
              slug: this.slugify(match[1])
            });
          }
        }
      }
    },

    slugify(text) {
      return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    },

    hasVoted(slug) {
      if (!window.RoadmapVoteService) return false;
      return window.RoadmapVoteService.hasVoted(this.voteData, slug);
    },

    getVoteCount(slug) {
      return this.voteData[slug]?.count || 0;
    },

    async toggleVote(slug) {
      if (!window.RoadmapVoteService || this.voting) return;

      this.voting = slug;
      try {
        const result = await window.RoadmapVoteService.toggleVote(slug);
        this.voteData[slug] = {
          count: result.count,
          voters: { [window.VoterIdService.getVoterId()]: result.hasVoted }
        };
        this.voteData = { ...this.voteData };
      } catch (e) {
        console.error('Vote failed:', e);
      }
      this.voting = null;
    },

    async submitSuggestion() {
      if (!this.suggestionText.trim() || this.submitting) return;
      if (!window.RoadmapVoteService) return;

      this.submitting = true;
      try {
        await window.RoadmapVoteService.submitSuggestion(this.suggestionText);
        this.suggestionText = '';
        this.submitted = true;
        setTimeout(() => { this.submitted = false; }, 3000);
      } catch (e) {
        console.error('Suggestion failed:', e);
      }
      this.submitting = false;
    }
  }));
});
;
// reference-data.js
// Alpine component for data-driven reference guide

// Static helper functions (called before Alpine component initializes)
function formatDateStatic(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTimeStatic(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function getDateExamplesStatic() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  return {
    now: formatDateTimeStatic(now),
    today: formatDateStatic(today),
    tomorrow: formatDateStatic(tomorrow),
    yesterday: formatDateStatic(yesterday)
  };
}

function getReferenceDataStatic(dateExamples) {
  return [
    // QUICK START - Featured section for new users
    {
      title: 'Quick Start',
      featured: true,
      items: [
        {
          topic: 'Basic Math',
          examples: [{ input: '2 + 2 * 3', output: '8' }]
        },
        {
          topic: 'Percentages',
          examples: [{ input: '20% of 150', output: '30' }]
        },
        {
          topic: 'Unit Conversion',
          examples: [{ input: '5 miles in km', output: '8.05' }]
        },
        {
          topic: 'Variables',
          stacked: true,
          examples: [
            { input: 'sales = 20k', output: '20,000' },
            { input: 'cost = 5k', output: '5,000' },
            { input: 'profit = sales - cost', output: '15,000' }
          ]
        },
        {
          topic: 'Date Math',
          examples: [{ input: 'today + 30 days', output: 'Feb 9, 2026' }]
        }
      ]
    },

    // BASICS
    {
      title: 'Basic Arithmetic',
      demo: 'https://instacalc.com/57484',
      items: [
        {
          topic: 'Arithmetic',
          subtitle: '+ - * x /',
          stacked: true,
          examples: [
            { input: '2 + 2 - 3', output: '1' },
            { input: '(3 * 5) / 2', output: '7.5' }
          ]
        },
        {
          topic: 'Roots & Powers',
          stacked: true,
          examples: [
            { input: 'sqrt(9)', output: '3' },
            { input: '5^2', output: '25' },
            { input: '8^(1/3)', output: '2', comment: 'cube root' },
            { input: '16^(3/4)', output: '8' }
          ],
          units: ['sqrt', 'cbrt']
        },
        {
          topic: 'Percent',
          stacked: true,
          examples: [
            { input: '5%', output: '0.05' },
            { input: '17% of 200', output: '34' },
            { input: '17% off 200', output: '166' },
            { input: '20 + 50%', output: '30' }
          ]
        },
        {
          topic: 'Modulo',
          examples: [
            { input: '5 mod 3', output: '2' }
          ]
        },
        {
          topic: 'Number Shortcuts',
          subtitle: 'k, M, b, t',
          stacked: true,
          examples: [
            { input: '3.3k', output: '3,300' },
            { input: '200 million', output: '200,000,000' },
            { input: '6.6b', output: '6,600,000,000' }
          ]
        },
        {
          topic: 'Scientific Notation',
          stacked: true,
          examples: [
            { input: '1.234e3', output: '1,234' },
            { input: '1e-3', output: '0.001' },
            { input: '1000 as sci', output: '1e3' }
          ]
        },
        {
          topic: 'Plain English',
          stacked: true,
          examples: [
            { input: 'fifteen plus five', output: '20' },
            { input: 'half of 27', output: '13.5' },
            { input: '15 squared', output: '225' },
            { input: '13 is what percent of 25', output: '52%' }
          ]
        },
        {
          topic: 'Alternate Symbols',
          stacked: true,
          examples: [
            { input: '12 × 3', output: '36' },
            { input: '12 ÷ 4', output: '3' },
            { input: 'πr²', output: '12.566…' }
          ],
          units: ['×', '÷', '·', 'π', '²', '³', '√', '∛', '∠']
        }
      ]
    },

    // VARIABLES & ROWS
    {
      title: 'Variables & Rows',
      demo: 'https://instacalc.com/57485',
      items: [
        {
          topic: 'Variables',
          stacked: true,
          examples: [
            { input: 'x = 9', output: '9' },
            { input: 'y = x + 3', output: '12' }
          ]
        },
        {
          topic: 'Multiple Statements',
          subtitle: 'use ; for one line',
          examples: [
            { input: 'a = 5; b = 10; a + b', output: '15' }
          ]
        },
        {
          topic: 'Row References',
          stacked: true,
          examples: [
            { input: 'r1', output: '9' },
            { input: 'r2', output: '12' },
            { input: 'r3', output: '3' },
            { input: 'sum(r1:r3)', output: '24' },
            { input: 'average(r1:r3)', output: '8' },
            { input: 'max(r1:r3)', output: '12' },
            { input: 'min(r1:r3)', output: '3' }
          ]
        }
      ]
    },

    // FORMATTING
    {
      title: 'Formatting',
      demo: 'https://instacalc.com/57487',
      items: [
        {
          topic: 'Currency',
          stacked: true,
          examples: [
            { input: '$5', output: '$5.00' },
            { input: '$5 / 3', output: '$1.67' },
            { input: '$$5 / 3', output: '1.67', comment: 'no symbol' }
          ],
          text: '<div class="info-box"><strong>$</strong> formats to .00 with symbol. <strong>$$</strong> rounds to .00, no symbol.</div>'
        },
        {
          topic: 'Comments',
          subtitle: 'use // or #',
          stacked: true,
          examples: [
            { input: '30 + 40 // note', output: '70' },
            { input: '# this is ignored', output: '' }
          ]
        },
        {
          topic: 'Comment Interpolation',
          subtitle: 'use {var}',
          stacked: true,
          fullWidth: true,
          examples: [
            { input: 'total = 50 // Total is {total} dollars' },
            { input: 'rate = 0.15 // Tax rate: {rate as percent}' }
          ],
          text: '<div class="info-box">Variables in <strong>{braces}</strong> are replaced with their values in the comment</div>'
        },
        {
          topic: 'Click result to collapse row',
          text: '<div class="space-y-3 mt-2"><div><div class="text-xs text-[var(--calc-comment-color)] opacity-60 mb-1">Hide computation details</div><div class="flex gap-3 text-xs"><div class="flex-1 px-3 py-2 rounded-lg bg-[color-mix(in_srgb,var(--calc-comment-color)_8%,transparent)]"><div class="flex justify-between"><span class="text-[var(--text)]">price = 200 * 3</span><span class="font-mono text-[var(--calc-result-color)]">600</span></div></div><div class="flex items-center text-[var(--calc-comment-color)]">→</div><div class="flex-1 px-3 py-2 rounded-lg bg-[color-mix(in_srgb,var(--calc-comment-color)_8%,transparent)]"><div class="flex justify-between items-center"><span class="text-[var(--calc-comment-color)]">price</span><span class="font-mono text-[var(--calc-result-color)]">600</span></div></div></div></div><div><div class="text-xs text-[var(--calc-comment-color)] opacity-60 mb-1">Create editable input</div><div class="flex gap-3 text-xs"><div class="flex-1 px-3 py-2 rounded-lg bg-[color-mix(in_srgb,var(--calc-comment-color)_8%,transparent)]"><div class="flex justify-between"><span class="text-[var(--text)]">quantity = 5</span><span class="font-mono text-[var(--calc-result-color)]">5</span></div></div><div class="flex items-center text-[var(--calc-comment-color)]">→</div><div class="flex-1 px-3 py-2 rounded-lg bg-[color-mix(in_srgb,var(--calc-comment-color)_8%,transparent)]"><div class="flex justify-between items-center gap-2"><span class="text-[var(--calc-comment-color)]">quantity</span><span class="px-2 py-0.5 rounded border border-[var(--border-color)] bg-[var(--bg)] font-mono text-[var(--calc-result-color)]">5</span></div></div></div></div></div>'
        },
        {
          topic: 'Insert/Remove Row',
          text: '<div class="info-box">Hover row label (<strong>R1</strong>) to display row options</div>'
        },
        {
          topic: 'Display Formats',
          desc: 'Use: as, to, into, ->',
          stacked: true,
          examples: [
            { input: '0x123 as decimal', output: '291' },
            { input: '.15 as percent', output: '15%' },
            { input: '.66 as fraction/10', output: '7/10' },
            { input: '.15 as fraction', output: '3/20' },
            { input: '2/3 as decimal', output: '0.666…' },
            { input: '15000 as money', output: '15K' },
            { input: '15000 as scientific', output: '1.5e4' },
            { input: '15000 as engineering', output: '15e3' },
            { input: '3.5 inches as tape measure', output: '3 1/2"' },
            { input: '100 as hex', output: '0x0064' },
            { input: '255 as octal', output: '0o377' },
            { input: '255 as roman', output: 'CCLV' },
            { input: '100 as bin', output: '0b01100100' },
            { input: '100 cm as value', output: '100' },
            { input: '100 cm as unit', output: '1 cm' },
            { input: '100 cm as tape measure', output: '3\' 3 3/8"' },
            { input: '1234567 cm as scaled', output: '12.3457 km' },
            { input: '1234567 as nearest 1000', output: '1,235,000' },
            { input: '1.2345 to 2 decimals', output: '1.23' },
            { input: '3661 seconds as time', output: '1h 1m 1s' }
          ],
          units: ['percent', 'fraction', 'decimal', 'money', 'scientific', 'engineering', 'roman', 'time', 'scaled', 'value', 'unit']
        }
      ]
    },

    // UNITS & CONVERSIONS
    {
      title: 'Units & Conversions',
      demo: 'https://instacalc.com/57486',
      items: [
        {
          topic: 'Convert Units',
          desc: 'Use: in, to, into, as, ->',
          examples: [
            { input: '15 feet into inches', output: '180', comment: 'inches' }
          ]
        },
        {
          topic: 'Unit Rates',
          desc: 'Use: /, per, every, until',
          stacked: true,
          examples: [
            { input: '15 miles/hour into feet/second', output: '22', comment: 'ft/s' },
            { input: '15 pounds per year in pounds per month', output: '1.25', comment: 'lbs/mo' }
          ]
        },
        {
          topic: 'Custom Units',
          stacked: true,
          examples: [
            { input: '15 sales / 2 people in sales per person', output: '7.5', comment: 'sales/person' },
            { input: '10 sales / 2 people in people until 1000 sales', output: '200', comment: 'people' },
            { input: '15 sales/day in minutes per sale', output: '96', comment: 'min/sale' }
          ]
        },
        {
          topic: 'Autoconversion',
          desc: 'Use: @, at, using, with, for',
          stacked: true,
          examples: [
            { input: '15 mph @ 10 hours', output: '150', comment: 'miles' },
            { input: '15 mph @ 100 miles', output: '6.667', comment: 'hours' },
            { input: '150 dollars/sale @ 10 sales/day @ 10 days', output: '150,000', comment: 'dollars' },
            { input: '150 dollars/sale @ 10 sales/day @ 750 dollars', output: '0.5', comment: 'day' }
          ]
        },
        {
          topic: 'Implied Conversion',
          stacked: true,
          examples: [
            { input: '15 mph in min', output: '0.25', comment: 'miles/min' },
            { input: '15 mph in /min', output: '0.25', comment: 'miles/min' },
            { input: '15 mph in km', output: '24.1', comment: 'km/hour' }
          ]
        },
        {
          topic: 'SI Units',
          stacked: true,
          examples: [
            { input: '100 J in kWh', output: '0.0000278 kWh' },
            { input: '1 hp in watts', output: '745.7 W' }
          ],
          units: ['N', 'J', 'W', 'kW', 'kWh', 'Pa', 'Hz', 'V', 'A', 'ohm', 'C', 'F']
        },
        {
          topic: 'Dimensional Analysis',
          stacked: true,
          fullWidth: true,
          examples: [
            { input: '15 kg * gravity in newton', output: '147.1 N' },
            { input: '30 miles per gallon in dollars per mile at $3/gallon', output: '0.1 dollars/mile' },
            { input: '(4 GB/s) / (4 GHz)', output: '1 byte/cycle' }
          ]
        },
        {
          topic: 'Weight',
          examples: [
            { input: '150 lbs in kg', output: '68.038', comment: 'kg' }
          ],
          units: ['kilogram', 'gram', 'milligram', 'microgram', 'metric ton', 'pound', 'ounce', 'stone', 'short ton', 'long ton']
        },
        {
          topic: 'Length',
          examples: [
            { input: '15 feet in inches', output: '180', comment: 'inches' }
          ],
          units: ['meter', 'kilometer', 'centimeter', 'millimeter', 'micrometer', 'nanometer', 'inch', 'foot', 'yard', 'mile', 'lightyear', 'astronomical unit', 'parsec']
        },
        {
          topic: 'Area',
          examples: [
            { input: '2 acres in m^2', output: '8093.712', comment: 'm^2' }
          ],
          units: ['square meter', 'square kilometer', 'square centimeter', 'square millimeter', 'square foot', 'square inch', 'square yard', 'square mile', 'acre', 'hectare']
        },
        {
          topic: 'Volume',
          examples: [
            { input: '1500 cc in cubic inches', output: '91.53', comment: 'cubic inches' }
          ],
          units: ['cubic meter', 'liter', 'milliliter', 'cubic centimeter', 'cubic foot', 'cubic inch', 'gallon', 'quart', 'pint', 'cup', 'fluid ounce', 'tablespoon', 'teaspoon']
        },
        {
          topic: 'Time',
          examples: [
            { input: '3 days in minutes', output: '4320', comment: 'minutes' }
          ],
          units: ['second', 'millisecond', 'microsecond', 'nanosecond', 'minute', 'hour', 'day', 'week', 'month', 'year', 'decade', 'century']
        },
        {
          topic: 'Currency',
          desc: 'updated weekly',
          examples: [
            { input: '15 USD in EUR', output: '12.69…', comment: 'EUR' }
          ],
          currencies: ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'INR', 'CAD', 'AUD', 'CHF', 'BTC']
        },
        {
          topic: 'Currency Shorthand',
          stacked: true,
          examples: [
            { input: '15 dollars in loonie' },
            { input: '200 yen in USD' },
            { input: '50 euro in pounds' }
          ],
          units: [
            'loonie',
            'yen',
            'euro',
            'pounds',
            'rupee',
            'yuan',
            'peso',
            'won',
            'franc',
            'aussie',
            'baht',
            'dirham'
          ]
        },
        {
          topic: 'Stock Lookup',
          examples: [
            { input: 'STOCK(AAPL)', output: '210.53', comment: 'last closing price' }
          ]
        },
        {
          topic: 'Tape Measure',
          stacked: true,
          examples: [
            { input: '5\' 3 1/2"', output: '5\' 3 1/2"', comment: 'feet + fractional inches' },
            { input: '3 3/8"', output: '3 3/8"', comment: 'just inches' },
            { input: '3\' 4" + 2\' 8"', output: '6\' 0"' },
            { input: '5\' 3 1/2" x 2', output: '10\' 7"' },
            { input: '180 cm as tape measure', output: '5\' 10 55/64"' },
            { input: '5\' 3 1/2" in cm', output: '161.29 cm' }
          ],
          units: ['as tape measure', 'as feet and inches']
        }
      ]
    },

    // DATE/TIME
    {
      title: 'Date & Time',
      demo: 'https://instacalc.com/57489',
      items: [
        {
          topic: 'Date Keywords',
          stacked: true,
          examples: [
            { input: 'now', output: dateExamples.now },
            { input: 'today', output: dateExamples.today },
            { input: 'tomorrow', output: dateExamples.tomorrow },
            { input: 'yesterday', output: dateExamples.yesterday }
          ]
        },
        {
          topic: 'Date Formats',
          stacked: true,
          examples: [
            { input: '1/15/2025', comment: 'M/D/Y' },
            { input: 'Jan 15, 2025', comment: 'Mon D, Y' },
            { input: 'Jan 15 2025', comment: 'Mon D Y' },
            { input: '2025/1/15', comment: 'Y/M/D' },
            { input: '25.12.2024', comment: 'D.M.Y (European)' },
            { input: '2024.12.25', comment: 'Y.M.D' },
            { input: 'date(2025, 1, 15)', comment: 'function' }
          ]
        },
        {
          topic: 'Date Math',
          stacked: true,
          examples: [
            { input: '1/1/90 + 200 days', output: 'Fri, Jul 20, 1990' },
            { input: 'Dec 31 - today', comment: 'days until end of year' }
          ]
        },
        {
          topic: 'Timezone Conversion',
          examples: [
            { input: '8pm PST in EST', output: '11pm EDT' }
          ]
        },
        {
          topic: 'Timespans',
          stacked: true,
          examples: [
            { input: '3h 15m', output: '3h 15m', comment: 'timespan' },
            { input: '3:15', output: '3h 15m', comment: 'hh:mm' },
            { input: '00:3:15', output: '3m 15s', comment: 'hh:mm:ss' }
          ]
        }
      ]
    },

    // MATH & SCIENCE
    {
      title: 'Scientific',
      demo: 'https://instacalc.com/50007',
      items: [
        {
          topic: 'Constants',
          stacked: true,
          examples: [
            { input: 'PI', output: '3.14159…' },
            { input: 'E', output: '2.71828…' },
            { input: 'PHI', output: '1.618…', comment: 'golden ratio' },
            { input: 'TAU', output: '6.283…', comment: '2π' }
          ]
        },
        {
          topic: 'Radians',
          desc: 'Default is degrees',
          stacked: true,
          examples: [
            { input: '30', output: '30' },
            { input: '30 deg', output: '30' },
            { input: 'PI rad', output: '180' }
          ]
        },
        {
          topic: 'Trig Functions',
          subtitle: "degrees by default, use 'rad' for radians",
          examples: [
            { input: 'sin(30)', output: '0.5' },
            { input: 'sin(PI/4 rad)', output: '0.707…' }
          ],
          units: ['sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'sinh', 'cosh', 'tanh', 'sec', 'csc', 'cot']
        },
        {
          topic: 'Exponents and Logs',
          stacked: true,
          examples: [
            { input: 'exp(2)', output: '7.389…' },
            { input: 'E^2', output: '7.389…' },
            { input: 'ln(2)', output: '0.693…' },
            { input: 'log(2)', output: '0.301…', comment: 'base 10' },
            { input: 'log_2(8)', output: '3' }
          ],
          units: ['exp', 'ln', 'log', 'log_2', 'log_10']
        },
        {
          topic: 'Complex Numbers',
          stacked: true,
          examples: [
            { input: '3 + 4i', outputHtml: '3 + 4<span class="imaginary-i">i</span>' },
            { input: '(3 + 4i) * (1 - 2i)', outputHtml: '11 - 2<span class="imaginary-i">i</span>' },
            { input: 'sqrt(-1)', outputHtml: '<span class="imaginary-i">i</span>' },
            { input: 'e^(pi * i)', output: '-1', comment: "Euler's identity" },
            { input: '5 ∠ 53.13', outputHtml: '3 + 4<span class="imaginary-i">i</span>', comment: 'phasor' }
          ]
        }
      ]
    },
    {
      title: 'Physics Constants',
      items: [
        {
          topic: 'Speed of Light',
          examples: [
            { input: 'c_light', output: '299,792,458 m/s' }
          ]
        },
        {
          topic: 'Gravity',
          examples: [
            { input: 'gravity', output: '9.8 m/s²' },
            { input: 'smallG', output: '9.8 m/s²' }
          ]
        },
        {
          topic: 'Gravitational Constant',
          examples: [
            { input: 'g_const', output: '6.67430×10⁻¹¹ N·m²/kg²' },
            { input: 'bigG', output: '6.67430×10⁻¹¹ N·m²/kg²' }
          ]
        },
        {
          topic: 'Planck Constant',
          examples: [
            { input: 'h_planck', output: '6.62607×10⁻³⁴ J·s' }
          ]
        },
        {
          topic: 'Avogadro Constant',
          examples: [
            { input: 'avogadro_const', output: '6.02214×10²³ /mol' }
          ]
        },
        {
          topic: 'Boltzmann Constant',
          examples: [
            { input: 'k_boltzmann', output: '1.38065×10⁻²³ J/K' }
          ]
        }
      ]
    },
    {
      title: 'Other Math',
      demo: 'https://instacalc.com/50013',
      items: [
        {
          topic: 'Random Numbers',
          stacked: true,
          examples: [
            { input: 'random', output: '0.434…', comment: 'from 0…1' },
            { input: 'random(10)', output: '7', comment: 'from 1…10' }
          ]
        },
        {
          topic: 'Rounding',
          stacked: true,
          examples: [
            { input: 'round(3.51)', output: '4' },
            { input: 'roundup(3.01)', output: '4' },
            { input: 'rounddown(3.99)', output: '3' }
          ],
          units: ['round', 'roundup', 'rounddown', 'floor', 'ceil', 'trunc']
        },
        {
          topic: 'Absolute Value',
          stacked: true,
          examples: [
            { input: 'abs(-2)', output: '2' },
            { input: '|-3|', output: '3' }
          ]
        },
        {
          topic: 'Factorial',
          examples: [
            { input: '3!', output: '6' }
          ]
        }
      ]
    },

    // PROGRAMMING
    {
      title: 'Computer Units',
      demo: 'https://instacalc.com/57488',
      items: [
        {
          topic: 'File Sizes',
          stacked: true,
          examples: [
            { input: '1 KB', output: '1024', comment: 'bytes (base 2)' },
            { input: '1 kbit', output: '1024', comment: 'bits (base 2)' },
            { input: '1 KB in bits', output: '8192' },
            { input: '10 GB / 5 MB', output: '2048' }
          ],
          units: ['KB', 'MB', 'GB', 'TB', 'PB', 'kbit', 'mbit', 'gbit', 'KiB', 'MiB', 'GiB'],
          text: '<div class="info-box"><strong>KB</strong>, <strong>kb</strong> = bytes. <strong>kbit</strong> = bits. Base 2 by default, use <strong>kb_si</strong> for base 10. <em>(I may consider kb=1000 if you say "kibibyte".)</em></div>'
        },
        {
          topic: 'Bandwidth',
          examples: [
            { input: '1.5 mbps', output: '1,500,000', comment: 'bits/sec' }
          ],
          units: ['bit/s', 'kbps', 'mbps', 'gbps', 'tbps', 'pbps']
        },
        {
          topic: 'Frequency',
          stacked: true,
          examples: [
            { input: '3.5 GHz', output: '3,500,000,000', comment: 'cycles/sec' },
            { input: '4 GHz in ns', output: '0.25', comment: 'ns/cycle' },
            { input: '200 MHz * 3 minutes', output: '36,000,000,000 cycles' }
          ],
          units: ['Hz', 'kHz', 'MHz', 'GHz', 'THz']
        },
        {
          topic: 'Compute (FLOPS)',
          stacked: true,
          examples: [
            { input: '1 TFLOPS', output: '1e12', comment: 'ops/sec' },
            { input: '100 GFLOPS * 5 sec', output: '500 Gop', comment: 'total ops' },
            { input: '1e15 ops / 2 PFLOPS', output: '500 ms' }
          ],
          units: ['FLOPS', 'KFLOPS', 'MFLOPS', 'GFLOPS', 'TFLOPS', 'PFLOPS', 'EFLOPS']
        },
        {
          topic: 'Time Formatting',
          stacked: true,
          examples: [
            { input: 'time(90)', output: '1m 30.00s' },
            { input: 'time(1gb / 1.5mbps)', output: '1h 31m 1.333s' }
          ]
        }
      ]
    },
    {
      title: 'Base Conversion',
      demo: 'https://instacalc.com/50008',
      items: [
        {
          topic: 'To Decimal',
          stacked: true,
          examples: [
            { input: '0b1001 as decimal', output: '9' },
            { input: '0o567 as dec', output: '375' },
            { input: '0xfeff as dec', output: '65,279' }
          ],
          units: ['bin', 'oct', 'hex', 'dec']
        },
        {
          topic: 'From Decimal',
          stacked: true,
          examples: [
            { input: '9 as binary', output: '0b00001001' },
            { input: 'oct(375)', output: '0o567' },
            { input: 'hex(65279)', output: '0xfeff' }
          ],
          units: ['binary', 'octal', 'hex', 'decimal']
        }
      ]
    },
    {
      title: 'Programming',
      demo: 'https://instacalc.com/50008',
      items: [
        {
          topic: 'Assignment Shorthand',
          stacked: true,
          examples: [
            { input: 'x = 3', output: '3' },
            { input: '++x', output: '4' },
            { input: 'x += 3', output: '7' }
          ]
        },
        {
          topic: 'Bitwise Operators',
          stacked: true,
          examples: [
            { input: 'bin(a = 3)', output: '0b00000011' },
            { input: 'bin(b = 5)', output: '0b00000101' },
            { input: 'a and b', output: '1' },
            { input: 'bin(a and b)', output: '0b00000001' },
            { input: 'a or b', output: '7' },
            { input: 'a xor b', output: '6' },
            { input: 'not a', output: '-4', comment: "two's complement" },
            { input: 'a << 2', output: '12', comment: 'left shift' },
            { input: 'b >> 1', output: '2', comment: 'right shift' }
          ],
          units: ['and', '&', 'or', '|', 'xor', 'not', '~', '<<', '>>', '>>>']
        },
        {
          topic: 'Logical Operators',
          stacked: true,
          examples: [
            { input: '2 * 2 > 3', output: 'true' },
            { input: 'x == 3', output: 'true' },
            { input: '2 * 2 > x', output: 'true' }
          ],
          units: ['>', '>=', '==', '<=', '<', '!=']
        },
        {
          topic: 'Conditionals',
          subtitle: 'if/else',
          fullWidth: true,
          smallText: true,
          spacious: true,
          examples: [
            { input: 'if (3 > 2) { "bigger" } else { "smaller or equal" }' },
            { input: 'if (temp > 75 and weather == "sunny") { "go to beach" }' },
            { input: 'if (temp < 0 or weather == "snowing") { "cold" } else { "not cold" }' },
            { input: 'if (x > 3 && x < 20) { "in range" } else { "out of range" }' },
            { input: 'if (score >= 90) { "A" } else if (score >= 80) { "B" } else if (score >= 70) { "C" } else { "F" }' }
          ]
        },
        {
          topic: 'Symbol Shortcuts',
          stacked: true,
          examples: [
            { input: '\\sqrt', output: '√' },
            { input: '\\alpha', output: 'α' },
            { input: '\\times', output: '×' },
            { input: 'x\\2', output: 'x²' },
            { input: '\\v = 20 m/s', output: '𝒗 = 20 m/s', comment: 'vector variable' }
          ],
          text: '<div class="mt-3 p-3 rounded-lg bg-[color-mix(in_srgb,var(--calc-comment-color)_6%,transparent)]"><div class="flex items-center gap-3 text-sm"><div class="flex items-center gap-1.5"><span class="px-2 py-1 rounded bg-[var(--bg)] border border-[var(--border-color)] font-mono text-[var(--text)]">\\sqrt</span><span class="text-[var(--calc-comment-color)]">+</span><span class="px-2 py-0.5 rounded bg-[var(--bg)] border border-[var(--border-color)] text-xs text-[var(--calc-comment-color)]">space</span></div><span class="text-[var(--calc-comment-color)]">→</span><span class="text-lg text-[var(--calc-result-color)]">√</span></div><p class="mt-2 text-xs text-[var(--calc-comment-color)] opacity-70">Type backslash + name, then space to insert symbol. <strong>\\letter</strong> → 𝒗 (bold/vector). <strong>𝒗</strong> and <strong>v</strong> are different variables.</p></div>',
          units: ['√', 'π', 'α', 'β', 'γ', 'δ', 'θ', '×', '÷', '²', '³']
        }
      ]
    },
    {
      title: 'Arrays & Objects',
      items: [
        {
          topic: 'Arrays',
          stacked: true,
          examples: [
            { input: 'prices = [10, 12.5, 9]', output: '[10, 12.5, 9]' },
            { input: 'sum(prices)', output: '31.5' }
          ]
        },
        {
          topic: 'Objects',
          stacked: true,
          fullWidth: true,
          examples: [
            { input: 'ball = { mass: 2 kg, v: 10 m/s }', output: '{…}' },
            { input: 'KE = 0.5 * ball.mass * ball.v^2', output: '100 J' }
          ]
        },
        {
          topic: 'Functions',
          stacked: true,
          fullWidth: true,
          examples: [
            { input: 'function tip(amount) { amount * 0.2 }' },
            { input: 'tip(85)', output: '17' },
            { input: '120 -> tip', output: '24', comment: 'pipe syntax' }
          ]
        }
      ]
    },

    // EXCEL FUNCTIONS
    {
      title: 'Excel Financial',
      demo: 'https://instacalc.com/56788',
      items: [
        {
          topic: 'Loan Payment',
          fullWidth: true,
          examples: [{ input: 'PMT(rate, nper, pv, [fv], [type])' }]
        },
        {
          topic: 'Interest Payment',
          fullWidth: true,
          examples: [{ input: 'IPMT(rate, per, nper, pv, [fv], [type])' }]
        },
        {
          topic: 'Principal Payment',
          fullWidth: true,
          examples: [{ input: 'PPMT(rate, per, nper, pv, [fv], [type])' }]
        },
        {
          topic: 'Present Value',
          fullWidth: true,
          examples: [{ input: 'PV(rate, nper, pmt, [fv], [type])' }]
        },
        {
          topic: 'Future Value',
          fullWidth: true,
          examples: [{ input: 'FV(rate, nper, pmt, [pv], [type])' }]
        },
        {
          topic: 'Internal Rate of Return',
          fullWidth: true,
          examples: [{ input: 'IRR(values, [guess])' }]
        },
        {
          topic: 'Net Present Value',
          fullWidth: true,
          examples: [{ input: 'NPV(rate, r1:r5)' }]
        },
        {
          topic: 'Interest Rate',
          fullWidth: true,
          examples: [{ input: 'RATE(nper, pmt, pv, [fv], [type], [guess])' }]
        },
        {
          topic: 'Number of Periods',
          fullWidth: true,
          examples: [{ input: 'NPER(rate, pmt, pv, [fv], [type])' }]
        },
        {
          topic: 'Interest (Specific Period)',
          fullWidth: true,
          examples: [{ input: 'ISPMT(rate, per, nper, pv)' }]
        },
        {
          topic: 'Rate of Return',
          fullWidth: true,
          examples: [{ input: 'RRI(nper, pv, fv)' }]
        },
        {
          topic: 'Duration',
          fullWidth: true,
          examples: [{ input: 'PDURATION(rate, pv, fv)' }]
        }
      ]
    },
    {
      title: 'Excel Statistical',
      demo: 'https://instacalc.com/56789',
      items: [
        {
          topic: 'Variance',
          sideBySide: true,
          examples: [{ input: 'var(r1:r3)' }, { input: 'var(x, y, z)' }]
        },
        {
          topic: 'Standard Deviation',
          sideBySide: true,
          examples: [{ input: 'stddev(r1:r3)' }, { input: 'stddev(x, y, z)' }]
        },
        {
          topic: 'Range',
          sideBySide: true,
          examples: [{ input: 'range(r1:r3)' }, { input: 'range(x, y, z)' }]
        },
        {
          topic: 'Count',
          sideBySide: true,
          examples: [{ input: 'count(r1:r3)' }, { input: 'count(x, y, z)' }]
        },
        {
          topic: 'Mean/Median/Mode',
          sideBySide: true,
          examples: [{ input: 'mean(r1:r3)' }, { input: 'median(r1:r3)' }, { input: 'mode(r1:r3)' }]
        },
        {
          topic: 'Percentile',
          sideBySide: true,
          examples: [{ input: 'percentile(r1:r3, p)' }, { input: 'percentile(x, y, z, p)' }]
        },
        {
          topic: 'Combination',
          sideBySide: true,
          examples: [{ input: 'combin(n, k)' }, { input: 'combination(n, k)' }]
        },
        {
          topic: 'Permutation',
          sideBySide: true,
          examples: [{ input: 'permut(n, k)' }, { input: 'permutation(n, k)' }]
        }
      ]
    },

    // VISUALIZATION
    {
      title: 'Charts',
      demo: 'https://instacalc.com/58522',
      image: '/img/reference/chart-examples.png',
      imageLink: 'https://instacalc.com/58522',
      items: [
        {
          topic: 'Chart Types',
          stacked: true,
          fullWidth: true,
          examples: [
            { input: 'barchart(r1:r3)' },
            { input: 'linechart(r1:r3)' },
            { input: 'piechart(r1:r3)' },
            { input: 'histogram([10, 12, 15, 30], 5)' }
          ]
        }
      ]
    },
    {
      title: 'Interactive Controls',
      demo: 'https://instacalc.com/59001',
      docs: 'https://github.com/kazad/instacalc/discussions/137',
      image: '/img/reference/ui-controls-examples.png',
      imageLink: 'https://instacalc.com/59001',
      items: [
        {
          topic: 'How to Use',
          text: '<div class="info-box">Add a control type in a comment, then collapse the row to see it.</div>'
        },
        {
          topic: 'Slider',
          fullWidth: true,
          examples: [{ input: 'volume = 50 // slider(0, 100, step: 5)' }]
        },
        {
          topic: 'Dropdown',
          fullWidth: true,
          examples: [{ input: 'plan = "basic" // dropdown("basic", "pro", "enterprise")' }]
        },
        {
          topic: 'Radio',
          fullWidth: true,
          examples: [{ input: 'mode = "linear" // radio("linear", "log", "sqrt")' }]
        },
        {
          topic: 'Checkbox',
          fullWidth: true,
          examples: [{ input: 'include_fees = true // checkbox()' }]
        },
        {
          topic: 'Date/Time',
          fullWidth: true,
          stacked: true,
          examples: [
            { input: 'meeting = 2025/11/06 // date()' },
            { input: '// time()' },
            { input: '// datetime()' }
          ]
        }
      ]
    },
    {
      title: 'Import & Sync',
      items: [
        {
          topic: 'Import JSON',
          fullWidth: true,
          examples: [{ input: 'data = import("https://example.com/data.json")' }],
          links: [{ label: 'Docs', url: 'https://github.com/kazad/instacalc/discussions/94' }]
        },
        {
          topic: 'Import Calc',
          fullWidth: true,
          examples: [
            { input: 'calc("https://instacalc.com/12345")' },
            { input: 'calc("My Shared Calc")' }
          ],
          links: [{ label: 'Docs', url: 'https://github.com/kazad/instacalc/discussions/95' }]
        }
      ]
    },

    // POWER USER
    {
      title: 'Power User Features',
      items: [
        {
          topic: 'Learn More',
          links: [
            { label: 'UI Controls', url: 'https://github.com/kazad/instacalc/discussions/137' },
            { label: 'Import/Export', url: 'https://github.com/kazad/instacalc/discussions/98' },
            { label: 'Objects & Arrays', url: 'https://github.com/kazad/instacalc/discussions/93' },
            { label: 'Remote JSON', url: 'https://github.com/kazad/instacalc/discussions/94' },
            { label: 'Calc Import', url: 'https://github.com/kazad/instacalc/discussions/95' },
            { label: 'String Interpolation', url: 'https://github.com/kazad/instacalc/discussions/42' },
            { label: 'Excel Functions', url: 'https://github.com/kazad/instacalc/discussions/87' },
            { label: 'Greek Letters', url: 'https://github.com/kazad/instacalc/discussions/30' },
            { label: 'Custom Formats', url: 'https://github.com/kazad/instacalc/discussions/110' }
          ]
        }
      ]
    }
  ];
}

document.addEventListener('alpine:init', () => {
  // Pre-compute data before Alpine component initializes
  // This ensures sections is populated before Alpine renders
  const dateExamples = getDateExamplesStatic();
  const sectionsData = getReferenceDataStatic(dateExamples);

  // Counter for unique instance IDs (prevents key collisions when template loads twice)
  let instanceCounter = 0;

  Alpine.data('referenceData', () => ({
    version: '',
    menuOpen: false,
    sections: sectionsData,
    instanceId: 'ref' + (++instanceCounter),  // Unique ID per instance

    init() {
      this.updateVersion();
    },

    updateVersion() {
      const lastModified = new Date(document.lastModified);
      const year = lastModified.getFullYear();
      const month = (lastModified.getMonth() + 1).toString().padStart(2, '0');
      const day = lastModified.getDate().toString().padStart(2, '0');
      this.version = `v${year}.${month}.${day}`;
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        // Brief visual feedback could be added here
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }));
});
